import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  HostBinding,
  inject,
  Inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  signal,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatMenuTrigger } from '@angular/material/menu';
import { translate, TranslocoService } from '@jsverse/transloco';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { GridsterConfig, GridsterItemComponent, GridsterPush } from 'angular-gridster2';

import { toCanvas } from 'html-to-image';
import { firstValueFrom, fromEvent, Observable, Subject } from 'rxjs';

import { catchError, debounceTime, distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';

import { DropdownObject } from '@inmoment/the-kitchen';

import { ActionPlanConstants, Constants } from 'app/constants/constants';
import {
  ChartDisplayOptions,
  WidgetActionSettings,
  WidgetDataSource,
  WidgetDriverAnalysisOptions,
} from 'app/models';
import {
  DocumentListSettingsDialogData,
  DocumentListSettingsDialogOptions,
} from 'app/modules/collection/components/document/document-list-settings-dialog/document-list-settings-dialog.component';
import { SaveDocumentsToCollectionOptions } from 'app/modules/collection/components/save-to-collection-dialog/save-to-collection-dialog.component';
import { ComparisonHandlerService } from 'app/modules/dashboard/services/comparison-handler-service';
import { SelectModeStore } from 'app/modules/dashboard/stores/select-mode.store';
import {
  WidgetGroupStore,
  isDashboardLevelWidgetGroup,
} from 'app/modules/dashboard/stores/widget-group.store';
import { CustomFieldsService } from 'app/modules/field/custom-fields.service';
import { ProjectCollection } from 'app/modules/project/models/project-collection.model';
import { PptxService } from 'app/modules/widget/services/pptx.service';
import { SemantriaAccount } from 'app/providers/bridge-api/account.service';
import { AuthService } from 'app/providers/bridge-api/auth.service';
import {
  Comment,
  CommentTarget,
  CommentType,
  Comparison,
  Dashboard,
  SectionFilter,
  Widget,
  WidgetGroup,
} from 'app/providers/bridge-api/dashboard.service';
import { GroupService } from 'app/providers/semantria-api/group.service';
import {
  AiInsightFeed,
  AiInsightsGroupFeedService,
} from 'app/providers/storage-api/ai-insights-group-feeds.service';
import { FieldService } from 'app/providers/storage-api/field.service';
import { LoggerService } from 'app/services/logger.service';
import { SectionService } from 'app/services/section.service';
import { SystemService } from 'app/services/system.service';
import { TimezoneService } from 'app/services/timezone.service';
import { ChartOptionUtils } from 'app/utils/chart-options.utils';

import { FilterUtils } from 'app/utils/filter.utils';
import { ActivityBarInteraction } from '../../../../components/dashboard-pill/dashboard-pill.component';
import {
  DashboardPillWrapperComponent,
  WidgetPillHoverState,
} from '../../../../components/dashboard-pill-wrapper/dashboard-pill-wrapper.component';
import { ModalCloseType } from '../../../../enums';
import {
  FieldStatisticsPayload,
  FieldStatisticsService,
} from '../../../../providers/field-statistics.service';
import { RealtimeMessage } from '../../../../providers/realtime.service';
import {
  AiInsight,
  AiInsightsService,
} from '../../../../providers/semantria-api/ai-insights.service';
import {
  MatchmakerHandler,
  MatchmakerProperties,
  SpotEService,
} from '../../../../providers/semantria-api/spote.service';
import { StorageAccountService } from '../../../../providers/storage-api/blender-account.service';
import {
  CollectionService,
  SectionMetadata,
} from '../../../../providers/storage-api/collection.service';
import {
  DEFAULT_PROJECT_COLOR_PALETTE,
  Project,
} from '../../../../providers/storage-api/project.service';
import { AlertService } from '../../../../services/alert.service';
import { DialogService } from '../../../../services/dialog.service';
import { Utils } from '../../../../utils/utils';
import {
  AnalyticItemFilterSets,
  DATE_TIME_WITHOUT_TIMEZONE_FORMAT,
  Filter,
  FilterService,
  FilterSet,
  getSectionFilters,
  SentimentFilterPolarity,
  UserSearchFilter,
} from '../../../collection/services/filter.service';
import { DashboardViewComponent } from '../../../dashboard/pages/dashboard-view/dashboard-view.component';
import {
  AnalyticItemTypeMap,
  AnalyticItemTypes,
} from '../../../dashboard/services/analytic-item-types';
import { ChartCustomColorService } from '../../../dashboard/services/chart-custom-color.service';
import { DashboardStore } from '../../../dashboard/stores/dashboard.store';
import { DatasourceManagerStore } from '../../../dashboard/stores/datasource-manager.store';
import { AnalyticItemsDialogOptions } from '../analytic-item-selector/analytic-item-selector.component';
import {
  BigNumberMetadataDialogOptions,
  BigNumberMetadataDialogResult,
  BigNumberMetadataSelectorDialogComponent,
} from '../big-number-metadata-selector-dialog/big-number-metadata-selector-dialog.component';
import { MAX_DATE_MARKER } from '../chart-options-dialog/chart-options-dialog.component';
import { CommentDialogOptions } from '../comment-dialog/comment-dialog.component';
import { CreateFeedModalInput } from '../create-insight-feed-dialog/create-insight-feed-dialog.component';
import {
  GridAnalyticItemSelectorDialogOptions,
  GridAnalyticItemSelectorDialogResult,
  setGridAnalyticItemSelectorDialogResultOnPayload,
} from '../grid-analytic-item-selector-dialog/grid-analytic-item-selector-dialog.component';
import {
  JourneyAnalyticItemSelectorDialogOptions,
  JourneyAnalyticItemSelectorDialogResult,
} from '../journey-analytic-item-selector-dialog/journey-analytic-item-selector-dialog.component';
import { NewspaperWidgletComponent } from '../newspaper-widglet/newspaper-widglet.component';
import { PredictiveDateRange } from '../predictive-widget-datepickers/predictive-widget-datepickers.component';
import { SelectedSection } from '../section-selector/section-selector.component';
import { WidgetAnalyticItemDialogResult } from '../widget-analytic-item-dialog/widget-analytic-item-dialog.component';
import { WidgetChartComponent, WidgetChartOptions } from '../widget-chart/widget-chart.component';
import { WidgetCrosstabComponent } from '../widget-crosstab/widget-crosstab.component';
import { WidgetDocumentListComponent } from '../widget-document-list/widget-document-list.component';
import { WidgetAiInsightsOptions } from '../widget-expansion-panel/widget-ai-insights/widget-ai-insights.component';
import { WidgetCommentListOptions } from '../widget-expansion-panel/widget-comment-list/widget-comment-list.component';
import {
  WidgetFilterDialogComponent,
  WidgetFilterDialogOptions,
} from '../widget-filter/widget-filter.component';
import { WidgetInsightFeedSource } from '../widget-insight-feed/widget-insight-feed.component';
import {
  WidgetSentimentFilterDialogComponent,
  WidgetSentimentFilterDialogOptions,
} from '../widget-sentiment-filter/widget-sentiment-filter.component';
import { ChartDataService } from '../../services/chart-data.service';
import { AbstractChartReport } from '../../services/chart-report/chart-report-abstract/chart-report-abstract';
import { ChartReportService } from '../../services/chart-report.service';
import { PredictiveWidgetDataService } from '../../services/predictive-widget-data.service';
import { WidgetAiInsightsService } from '../../services/widget-ai-insights.service';
import { WidgetCommentsHandlerService } from '../../services/widget-comments-handler.service';
import { WidgetService } from '../../services/widget.service';
import { AbstractWidgetComponent } from './abstract-widget.component';

enum CondenseLevel {
  NONE,
  DATASOURCE,
  WIDGET_GROUP,
  COMPARISON,
  ACTIONS,
}

export function copyWidgetGroupFilters(
  widgetGroup: WidgetGroup,
  currentWidgetId: string
): AnalyticItemFilterSets {
  const filterSet: AnalyticItemFilterSets = { ...widgetGroup.filters };

  if (widgetGroup.parentWidget && widgetGroup.parentWidget !== currentWidgetId) {
    Object.keys(widgetGroup.parentFilters).forEach((key) =>
      filterSet[key].and.push(...widgetGroup.parentFilters[key].and)
    );
  }

  return filterSet;
}

export function hasSectionFilters(widget: Widget): boolean {
  return !!getSectionFilters(widget.payload.filters).length;
}

@Component({
  selector: 'app-widget',
  templateUrl: './widget.component.html',
  styleUrls: ['./widget.component.scss'],
  providers: [WidgetAiInsightsService],
  host: {
    '[class.note-editing]': 'isNote && editNote === "edit"',
  },
  standalone: false,
})
export class WidgetComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  // Widget expansion constants
  private static readonly EXPANSION_PANEL_WIDTH_COLS = 3;
  private static readonly EXPANSION_PANEL_EXTRA_WIDTH_PX = 30;

  @Input() dashboard: Dashboard;
  @Input() widget: Widget<any>;
  @Input() detailsHidden: boolean;
  @Input() isCustomSortMode;
  @Input() isReadOnly: boolean;
  @Input() canAddWidget: boolean;
  @Input() comparisonList: Comparison[];
  @Input() widgetGroup: WidgetGroup;
  @Input() comparison: Comparison;
  @Input() hasSmartSummaryResources: boolean;
  @Input() @HostBinding('class.has-changes') hasChanges: boolean;
  @Input() dashboardOptions: GridsterConfig;
  @Input() project: Project;

  private readonly widgetGroupStore = inject(WidgetGroupStore);
  readonly selectModeStore = inject(SelectModeStore);
  readonly datasourceManagerStore = inject(DatasourceManagerStore);
  readonly dashboardStore = inject(DashboardStore);
  private readonly logger = inject(LoggerService);

  @Output() isCustomSortModeChange = new EventEmitter();
  @Output() syncItemColors = new EventEmitter();
  @Output() resetItemColor = new EventEmitter<string>();
  @Output() addWidget = new EventEmitter();
  @Output() cloneWidget = new EventEmitter();
  @Output() deleteWidget = new EventEmitter();
  @Output() moveWidget = new EventEmitter();
  @Output() widgetLoading = new EventEmitter<boolean>();
  @Output() widgetLoaded = new EventEmitter<boolean>();
  @Output() updateWidgetGroupMembership = new EventEmitter();
  @Output() updateWidgetGroupParent = new EventEmitter();
  @Output() addDateFilterToWidgetGroup = new EventEmitter();
  @Output() openApplyComparison = new EventEmitter();
  @Output() openCreateDriverAnalysis = new EventEmitter<WidgetDriverAnalysisOptions>();
  @Output() onActivitybarInteraction = new EventEmitter<
    ActivityBarInteraction<WidgetDataSource | WidgetGroup | Comparison>
  >();
  @Output() ignoreWidgetChange = new EventEmitter<boolean>();
  @Output() beforeExpansionPanelOpen = new EventEmitter<string>();

  @ViewChildren('widgetComponent') private elementComponentList: QueryList<
    AbstractWidgetComponent<any>
  >;
  @ViewChild('actionswrapper') actionsWrapper: ElementRef;
  @ViewChild('widgetMenuTrigger') widgetMenuTrigger: MatMenuTrigger;
  @ViewChild('predictiveWidgetDatePicker')
  predictiveWidgetDatePicker: ElementRef;
  @ViewChild('widgetContainer') widgetContainer: ElementRef;
  @ViewChild('comparisonPill') set _comparisonPill(comparisonPill: ElementRef) {
    this.comparisonPill = comparisonPill;

    if (this.comparisonPill) {
      this.setComparisonPillColor();
    }
  }
  @ViewChild(DashboardPillWrapperComponent) pillWrapper: DashboardPillWrapperComponent;
  @ViewChild(NewspaperWidgletComponent, { read: ElementRef }) newspaperWidgletElement: ElementRef;
  @ViewChild(NewspaperWidgletComponent) newspaperWidglet: NewspaperWidgletComponent;
  @ViewChild('commentListContainer') commentListContainer: ElementRef;
  @ViewChild('aiInsightsContainer') aiInsightsContainer: ElementRef;

  comparisonPill: ElementRef;
  pillColoringInterval;

  readonly isPartOfOtherWidgetGroup = computed(() => {
    if (this.selectModeStore.mode() === 'widgetGroupBulkEdit' && this.widgetGroups) {
      const selectedGroupId = this.selectModeStore.data().widgetGroup.id;
      const otherGroups = this.widgetGroups.filter((group) => group.id !== selectedGroupId);
      for (const group of otherGroups) {
        if (group.widgets.some((item) => item === this.widget.id)) {
          return true;
        }
        if (group.parentWidget === this.widget.id) {
          return true;
        }
      }
    }
    return false;
  });

  readonly isWidgetSelectable = computed(() => {
    switch (this.selectModeStore.mode()) {
      case 'widgetGroup':
        return (
          this.canFilter &&
          this.isDocumentFilterGroupSelectEnabled &&
          !this.isPartOfOtherWidgetGroup() &&
          !this.widgetGroup
        );
      case 'widgetGroupBulkEdit':
        if (this.selectModeStore.isActivityBarSelectAction()) {
          return (
            this.canFilter &&
            this.isDocumentFilterGroupSelectEnabled &&
            (!this.selectModeStore.data().widgetGroup.isImpact || this.canAddToIPWG)
          );
        }
        return (
          this.canFilter &&
          this.isDocumentFilterGroupSelectEnabled &&
          !this.isPartOfOtherWidgetGroup() &&
          (!this.selectModeStore.data().widgetGroup.isImpact || this.canAddToIPWG) &&
          !(
            hasSectionFilters(this.widget) &&
            getSectionFilters(this.selectModeStore.data().widgetGroup.filters).length
          )
        );
      case 'filters':
        return (
          this.canFilter &&
          this.isDocumentFilterGroupSelectEnabled &&
          this.selectModeStore.selected()[0]?.id !== this.widget.id &&
          !this.widget.payload.isSpecial
        );
      case 'edit':
        return !this.widget.payload.isSpecial && !this.isPredictive;
      case 'comparison':
        if (this.selectModeStore.isActivityBarSelectAction()) {
          return this.canApplyComparison(this.selectModeStore.data().comparison);
        }
        return (
          this.comparison?.id === this.selectModeStore.data().comparison.id ||
          (this.canApplyComparison(this.selectModeStore.data().comparison) && !this.comparison)
        );
      case 'analyticItem':
        return ActionPlanConstants.SELECTABLE_RATING_WIDGET_TYPES.includes(
          this.widget.payload.type
        );
      case 'goal':
        return this.currentReport?.canSetChartGoal();
      case 'dateMarker':
        const chartPayload = this.widget.payload as WidgetChartOptions;
        return (
          this.currentReport?.canSetDateMarkers() &&
          chartPayload &&
          (!chartPayload.dateMarkers || chartPayload.dateMarkers.length < MAX_DATE_MARKER)
        );
      case 'weight':
        return this.canWeigh();
      case 'addFilter':
        return (
          this.canFilter &&
          this.isDocumentFilterGroupSelectEnabled &&
          !this.widget.payload.isSpecial
        );
      default:
        return false;
    }
  });

  readonly widgetSelectionText = computed(() => {
    if (this.selectModeStore.isActivityBarSelectAction()) {
      if (this.isWidgetSelectable()) {
        switch (this.selectModeStore.mode()) {
          case 'comparison':
            if (
              this.comparison &&
              this.comparison.id !== this.selectModeStore.data().comparison.id
            ) {
              return translate('widgets.select-replace-existing-comparison');
            }
            return '';
          case 'widgetGroupBulkEdit':
            if (
              this.widgetGroup &&
              this.widgetGroup?.id !== this.selectModeStore.data().widgetGroup.id
            ) {
              return translate('widgets.select-replace-existing-widget-group');
            }
            break;
          default:
            break;
        }
      } else if (!this.isWidgetSelectable()) {
        switch (this.selectModeStore.mode()) {
          case 'comparison':
            return this.getComparisonErrorMessage(
              this.selectModeStore.data()?.comparison,
              'widgets.comparison-not-applicable'
            );
        }
      }
    }
    if (this.isWidgetSelectable()) {
      return '';
    }
    switch (this.selectModeStore.mode()) {
      case 'widgetGroup':
      case 'widgetGroupBulkEdit':
        if (
          this.widget.payload.type === 'note' ||
          this.widget.payload.type === 'noteMarkdown' ||
          this.widget.payload.type === 'predictor' ||
          this.widget.payload.type === 'insightFeed' ||
          this.widget.payload.type === 'journey'
        ) {
          return translate('widgets.widget-group-not-applicable');
        } else if (
          hasSectionFilters(this.widget) &&
          getSectionFilters(this.selectModeStore.data().widgetGroup.filters).length
        ) {
          return translate('widgets.widget-group-not-applicable-selected');
        }
        return translate('widgets.already-has-widget-group');
      case 'filters':
        if (this.selectModeStore.selected()[0]?.id !== this.widget.id) {
          return translate('widgets.filter-not-applicable');
        }
        return '';
      case 'edit':
        return translate('widget.not-moveable');
      case 'comparison':
        if (this.comparison) {
          return translate('widgets.already-has-comparison');
        }
        return this.getComparisonErrorMessage(
          this.selectModeStore.data()?.comparison,
          'widgets.comparison-not-applicable'
        );
      case 'dateMarker':
        const chartPayload = this.widget.payload as WidgetChartOptions;
        if (chartPayload && chartPayload.dateMarkers?.length === MAX_DATE_MARKER) {
          return translate('widgets.date-marker-not-applicable');
        }
        return '';
      default:
        return '';
    }
  });

  predictiveDateRange: PredictiveDateRange;

  private elementComponent: AbstractWidgetComponent<any>;
  widgetDataSource: WidgetDataSource;
  resizeObservable: Observable<Event>;
  contentLimitChange$ = new Subject();
  unsub$ = new Subject<void>();

  widgetActions: {
    [key: string]: WidgetActionSettings;
  } = {};

  showWidgetMenu = true;

  widgetTitle: string;
  comparisonTooltip;

  widgetContentLimit: number;
  analyticItemTypes: AnalyticItemTypeMap = {};
  currentReport: AbstractChartReport;

  private readonly widgetActionsEps = 5;
  private activePopover: NgbPopover;

  showWidgetChartOptionsModal = false;
  showWidgetWidgetGroupModal = false;
  addingToWidgetGroup = false;
  adjustingFilterGroupParent = false;
  chartOptionsData: ChartDisplayOptions;
  showAddFilterToWidgetGroupModal = false;
  addFilterAvailableWidgetGroups = [];

  hasWidgetGroups = false;
  isWidgetGroupParent = false;
  canBeParentOfFilterGroup = false;
  enabledToBeParentOfFilterGroup = false;
  isShowAsDocumentList = false;
  isShowAsDataTable = false;
  isNote = false;
  canShowDocumentList = false;
  canShowDataTable = false;
  isDocumentFilterGroupSelectEnabled = true;
  canCreateCollection = false;
  canDownload = false;
  canDownloadExcel = false;
  canDownloadCSV = false;
  canDownloadPPTX = false;
  canDownloadImage = false;
  canDownloadTable = false;
  isLocked = false;
  elementHasWidgetGroup = false;
  canFilter = false;
  filterDisabled = false;
  canBeDeleted = true;
  canBeCloned = true;
  cloneWidgetTooltip: string;
  isWidgetDisabled = false;
  canSetAsParent = true;
  defaultTitle = '';
  hasAnalyses = false;
  hasFilters = false;
  hasInvalidComparison = false;
  isPredictive = false;
  isImpact = false;
  canAddToIPWG = true;
  canRemoveFromIPWG = true;
  canHideTitle = false;
  removeFromIPWGTooltip: string;

  backgroundColor = '#ffffff';
  condenseLevel: CondenseLevel = CondenseLevel.NONE;
  lastCondenseOperation: 'expand' | 'contract';
  isDrawingActionItems;

  hasDriverAnalysisFeature;
  driverAnalysisTooltip: string;

  isImageExporting;

  showDocListSettingsModal: boolean;
  docListSettingsOptions: DocumentListSettingsDialogOptions;

  showAnalyticItemModal: boolean;
  analyticItemModalOptions: AnalyticItemsDialogOptions;
  showCrosstabAnalyticItemsModal: boolean;
  crosstabAnalyticItemModalOptions: AnalyticItemsDialogOptions;

  gridAnalyticItemSelectorDialogOptions: GridAnalyticItemSelectorDialogOptions;
  showGridAnalyticItemSelectorDialog = false;

  journeyAnalyticItemSelectorDialogOptions: JourneyAnalyticItemSelectorDialogOptions;
  showJourneyAnalyticItemSelectorDialog = false;

  interactiveSmartSummaryEnabled = false;
  canAddComment;

  commentListModalOptions: WidgetCommentListOptions;

  showEditCommentModal: boolean;
  commentModalOptions: CommentDialogOptions;
  editNote: 'view' | 'edit' = 'view';
  widgetMenuDisplayOnHover = false;
  hoverState: WidgetPillHoverState = {
    entity: null,
    isHovered: false,
  };
  showCommentList = false;
  originalWidget: Widget<any>;

  newspaperData;
  previousCommentVisibilityState?: boolean;

  hasAiInsightFeature;
  showAiInsights;
  aiInsightsOptions: WidgetAiInsightsOptions;
  aiInsightsSelectedPoints: any[];

  sectionMetadata: SectionMetadata[] = [];
  insightFeeds: AiInsightFeed[] = [];
  insightFeedSourceOptions: DropdownObject[] = [];
  selectedInsightFeed: AiInsightFeed;
  insightFeedsortInsightToggle = false;
  selectedInsightFeedItems: AiInsight[] = [];
  showInsightFeedModal = false;
  insightFeedModalOptions: CreateFeedModalInput;

  showSaveDocumentsToCollectionModal = false;
  saveDocumentsToCollectionModalOptions: SaveDocumentsToCollectionOptions;

  onAfterPillCompact = this.afterPillCompactFn();
  onBeforePillCompact = this.beforePillCompactFn();
  collectionService = inject(CollectionService);
  fieldStatisticsService = inject(FieldStatisticsService);

  get widgetComponent(): WidgetComponent {
    return this;
  }

  get shouldShowTitleAsterisk(): boolean {
    return this.hasChanges && !this.dashboardViewComponent.activeSavedView;
  }

  get widgetNeedsOrHasExtension(): boolean {
    return this.originalWidget.cols < 9;
  }

  get widgetHasHorizontalDisplacement(): boolean {
    return this.originalWidget.x !== this.widget.x;
  }

  get visibleCommentsCount(): number {
    return this.elementComponent.getVisibleComments().length;
  }

  get excludedValues() {
    if (!this.widget.payload.excludedValues) {
      this.widget.payload.excludedValues = [];
    }
    return this.widget.payload.excludedValues;
  }

  kdaMinQuestionNum: number;
  fieldStatisticsPayload: FieldStatisticsPayload;
  availablefieldsForKda: Array<string>;

  get canCreateKda(): boolean {
    return this.availablefieldsForKda?.length > this.kdaMinQuestionNum;
  }
  analyticItemModalWidth = signal<string>(Constants.ANALYTIC_ITEM_MODAL_DEFAULT_MAX_WIDTH);

  constructor(
    private pptxService: PptxService,
    private systemService: SystemService,
    private dialogService: DialogService,
    private filterService: FilterService,
    private accountService: StorageAccountService,
    private account: SemantriaAccount,
    private chartReportService: ChartReportService,
    private predictiveWidgetDataService: PredictiveWidgetDataService,
    private alertService: AlertService,
    private comparisonService: ComparisonHandlerService,
    @Inject(DOCUMENT) private document: Document,
    private elementRef: ElementRef,
    private dashboardViewComponent: DashboardViewComponent,
    public gridsterItem: GridsterItemComponent,
    private translocoService: TranslocoService,
    private authService: AuthService,
    private widgetService: WidgetService,
    private zone: NgZone,
    private cdr: ChangeDetectorRef,
    private timezoneService: TimezoneService,
    private customFieldService: CustomFieldsService,
    private httpClient: HttpClient,
    private commentsHandlerService: WidgetCommentsHandlerService,
    private spoteService: SpotEService,
    private aiInsightsService: AiInsightsService,
    private aiInsightsGroupFeedService: AiInsightsGroupFeedService,
    private widgetAiInsightsService: WidgetAiInsightsService,
    private sectionService: SectionService,
    private groupService: GroupService
  ) {
    this.widgetAiInsightsService.handlerScenes$
      .pipe(
        takeUntilDestroyed(),
        filter((res) => !!res),
        catchError(() => {
          this.updateWidgetAction('aiInsights', {
            tooltip: translate('widgets.ai-insights.no-scenes'),
          });
          return [];
        })
      )
      .subscribe((res) => {
        this.updateAiInsightsAction(res);
      });
  }

  get isWidgetReadOnly(): boolean {
    return this.isReadOnly || this.isWidgetDisabled;
  }

  get aboutWidgetTypeKey(): string {
    const type = this.widget.payload.type;
    const isOverTime = ChartOptionUtils.isOverTimeAggregator(this.widget.payload.aggregator);

    if ((this.widget.payload as any).isDrilledDown && (this.widget.payload as any).subjectValue) {
      return 'journey';
    }

    if (type === 'documentCount' && isOverTime) {
      return 'volumeOverTime';
    }
    if (type === 'sentimentScore' && isOverTime) {
      return 'sentimentOverTime';
    }
    return type;
  }

  async ngOnInit() {
    this.resizeObservable = fromEvent(window, 'resize');
    this.resizeObservable
      .pipe(debounceTime(1000), distinctUntilChanged(), takeUntil(this.unsub$))
      .subscribe(() => {
        this.redrawWidget();
      });
    this.contentLimitChange$
      .pipe(debounceTime(800), distinctUntilChanged(), takeUntil(this.unsub$))
      .subscribe((value) => {
        this.closeContentLimitPopover(value as { $event: CustomEvent; popover: NgbPopover });
      });

    this.predictiveWidgetDataService.onSpecialImpactUnhideChanged
      .pipe(
        filter((d) => d.id !== this.widget.id && this.widget.payload.isSpecial),
        takeUntil(this.unsub$)
      )
      .subscribe((d) => {
        this.removeHiddenItemByIdx(d.idx);
      });

    this.comparisonService.comparisonApplied$
      .pipe(
        filter((res) => res.widgetId === this.widget.id),
        takeUntil(this.unsub$)
      )
      .subscribe((res) => this.onComparisonApplied(res.comparison, true));

    this.comparisonService.comparisonRevoked$
      .pipe(
        filter((res) => res.widgetId === this.widget.id),
        takeUntil(this.unsub$)
      )
      .subscribe(() => this.onComparisonRevoked());

    this.widget.payload.refresh = this.refreshWidget.bind(this);
    if (this.widget.dataSourceId) {
      this.widgetDataSource = this.datasourceManagerStore.getById(this.widget.dataSourceId);
      this.datasourceManagerStore.registerDataSourceToWidget(
        this.widget.id,
        this.widget.dataSourceId
      );
      await this.getAnalyticItemsMap();
      this.checkWeightFieldExists(this.widgetDataSource);
    }
    this.currentReport = this.getReport();
    FieldService.fillFieldDisplayNameForWidgets(this.widget.payload);

    this.excludedValues.sort((a, b) =>
      (a.displayName || a.name || a).localeCompare(b.displayName || b.name || b)
    );

    if (!this.widget.payload.userSelectedDateRange) {
      this.widget.payload.userSelectedDateRange = false;
    }
    if (this.comparison) {
      this.onComparisonApplied(this.comparison);
    }
    if (this.isInsightFeed()) {
      this.loadInsightFeeds(
        this.widget.payload.feedSource as WidgetInsightFeedSource,
        this.widget.payload.feedQuery
      );

      this.insightFeedsortInsightToggle = false;
    }

    this.translocoService.selectTranslateObject('analytic-items').subscribe(() => {
      this.onUpdateTitle();
    });

    this.updateDriverAnalysisProperties(this.widgetDataSource);

    this.updateTemplateBooleans();
  }

  async ngAfterViewInit() {
    this.elementComponent = this.elementComponentList.first;
    this.elementComponent.widgetDataSource = this.widgetDataSource;
    this.hasAiInsightFeature = await this.account.hasAccountFeature('SPOTLIGHT_MATCHMAKER');
    this.updateTemplateBooleans();
    this.initWidgetActions();
    if (this.widgetDataSource) {
      this.getAndUpdateIfWidgetCanCreateKda();
    }
    this.showWidgetMenu = this.calculateShowWidgetMenu();

    await this.setInteractiveSmartSummaryEnabled();

    // Don't even ask...
    this.elementComponentList.changes.subscribe(() => {
      setTimeout(async () => {
        if (this.elementComponent !== this.elementComponentList.first) {
          this.elementComponent = this.elementComponentList.first;
          this.pillWrapper.checkCompactSize();
        }

        if (!this.elementComponent.widgetDataSource) {
          this.elementComponent.widgetDataSource = this.widgetDataSource;
          this.elementComponent.widgetDataSourceId = this.widgetDataSource.id;
        }

        if (this.elementComponent.widgetLoadController.signal.aborted) {
          return;
        }
      });
    });
  }

  private async getAndUpdateIfWidgetCanCreateKda() {
    if (this.isNote || this.widgetDataSource.type === 'group' || this.widgetDataSource.isSocial) {
      // If the datasource changes and KDA is not available then we must reset the KD fields and properties
      this.availablefieldsForKda = [];
      this.updateDriverAnalysisProperties(this.widgetDataSource);
      return;
    }
    const kdaPreReq = await this.getKdaPrerequisites();
    const { projectCollection, query, customFields } = kdaPreReq;
    const kdaAccAndField = await Promise.all([
      this.account.sub.latest(),
      this.getFieldStatictics(projectCollection, query, customFields),
    ]);
    this.kdaMinQuestionNum =
      kdaAccAndField[0].storageLimits['DRIVER_ANALYSIS_DRIVER_QUESTION_SIZE_MIN'];
    this.fieldStatisticsPayload = kdaAccAndField[1];
    this.availablefieldsForKda = this.getAvailableFieldsForKdaCreation(this.fieldStatisticsPayload);
    this.updateDriverAnalysisProperties(this.widgetDataSource);
  }

  updateTemplateBooleans() {
    this.updateWidgetGroupsExist();
    this.updateWidgetGroupsParentCheck();
    this.updateCanBeParentOfFilterGroup();
    this.isNote =
      this.widget.payload.type === 'note' || this.widget.payload.type === 'noteMarkdown';
    this.isShowAsDataTable = this.widget.properties.as_data_table;
    this.canShowDocumentList = this.elementComponent?.canShowDocumentList();
    this.canShowDataTable = this.elementComponent?.canShowDataTable();
    this.isDocumentFilterGroupSelectEnabled =
      this.elementComponent?.isDocumentFilterGroupSelectEnabled();
    this.canCreateCollection =
      !this.isWidgetReadOnly && this.elementComponent?.canCreateCollection();
    this.canDownload = this.elementComponent?.canDownload;
    this.canDownloadExcel = this.elementComponent?.downloadFormats.indexOf('excel') >= 0;
    this.canDownloadCSV = this.elementComponent?.downloadFormats.indexOf('csv') >= 0;
    this.canDownloadPPTX = this.elementComponent?.downloadFormats.indexOf('pptx') >= 0;
    this.canDownloadImage = this.elementComponent?.downloadFormats.indexOf('image') >= 0;
    this.canDownloadTable = this.elementComponent?.downloadFormats.indexOf('table') >= 0;
    this.isLocked = this.elementComponent?.isLocked;
    this.elementHasWidgetGroup = this.elementComponent?.hasWidgetGroup;
    this.canFilter = this.elementComponent?.canFilter;
    this.filterDisabled = this.elementComponent?.filterDisabled;
    this.canBeDeleted =
      !this.isReadOnly && !this.isLocked && (this.elementComponent?.canBeDeleted ?? true);
    this.canBeCloned =
      !this.isWidgetReadOnly && !this.isLocked && this.elementComponent?.canBeCloned;
    this.defaultTitle = this.elementComponent?.defaultTitle ?? '';
    this.hasAnalyses = this.elementComponent?.hasAnalyses;
    this.hasFilters = this.elementComponent?.hasFilters;
    this.hasInvalidComparison = this.elementComponent?.hasInvalidComparison;
    this.isPredictive = this.widget.component === 'DashboardWidgetPredictiveComponent';
    this.backgroundColor = this.elementComponent?.backgroundColor ?? '#ffffff';
    this.canRemoveFromIPWG = this.elementComponent?.canRemoveFromIPWG;
    this.removeFromIPWGTooltip = this.elementComponent?.removeFromIPWGTooltip;
    this.canAddComment = !this.isWidgetReadOnly && this.elementComponent?.canAddComment;
    this.widgetMenuDisplayOnHover = this.elementComponent?.widgetMenuDisplayOnHover();
    this.canHideTitle = this.elementComponent?.canHideTitle();
    this.updateIsWidgetDisabled();
    this.updateCloneWidgetTooltip();
  }

  private async setInteractiveSmartSummaryEnabled(): Promise<void> {
    try {
      const hasFeature = await this.account.hasAccountFeature('INTERACTIVE_SMART_SUMMARIES');
      this.interactiveSmartSummaryEnabled = hasFeature && this.hasSmartSummaryResources;
    } catch (error) {
      this.logger.error('Error setting interactiveSmartSummaryEnabled:', { error });
      this.interactiveSmartSummaryEnabled = false;
    }
  }

  private onComparisonApplied(res, refresh?) {
    this.comparison = res;
    this.comparisonTooltip = undefined;

    this.updateAiInsightsAction();

    if (!this.comparison) {
      return;
    }

    this.elementComponent?.applyComparison(res);

    const tooltipText = this.comparison.filters
      .map((f) => this.comparisonService.filterDisplayValueExtractorFn(this.comparison.type)(f))
      .join('\n');

    this.comparisonTooltip = {
      header: this.comparison.title,
      text: tooltipText,
    };

    setTimeout(() => {
      if (refresh) {
        this.refreshWidget();
      }

      this.setComparisonPillColor();
    }, 10);
  }

  private onComparisonRevoked() {
    if (this.comparison) {
      this.comparison = undefined;
      delete this.widget.payload.comparison;
    }

    this.elementComponent?.revokeComparison();

    this.updateAiInsightsAction();
    this.dashboardStore.markDirty();

    setTimeout(() => {
      this.refreshWidget();
    }, 10);
  }

  private setComparisonPillColor() {
    this.pillColoringInterval = setInterval(() => {
      if (!this.comparison || !this.comparisonPill) {
        return;
      }

      const pillBtn = this.comparisonPill.nativeElement.querySelector('button');

      if (pillBtn) {
        clearInterval(this.pillColoringInterval);

        pillBtn.style.setProperty('--kxi-btn-fill-bg-inactive', this.comparison.color);
        pillBtn.style.setProperty('--kxi-btn-fill-bg-active', this.comparison.color);
        pillBtn.style.setProperty('--kxi-btn-fill-bg-hover', this.comparison.color);
      }
    }, 200);
  }

  predictiveDateRangeChanged(event: PredictiveDateRange) {
    this.predictiveDateRange = event;
  }

  ngOnChanges(changes: SimpleChanges) {
    this.canAddToIPWG =
      this.widget.payload.type !== 'documentCount' ||
      (this.widget.payload.aggregator !== 'time' && this.widget.payload.graphType !== 'pie');
    this.canRemoveFromIPWG = this.elementComponent?.canRemoveFromIPWG;
    this.removeFromIPWGTooltip = this.elementComponent?.removeFromIPWGTooltip;

    if (changes.detailsHidden && !changes.detailsHidden.firstChange) {
      setTimeout(() => this.pillWrapper.checkCompactSize(), 300);
    }
    if (changes.widget) {
      this.originalWidget = Utils.deepCloneWithNulls(this.widget);
    }
  }

  redrawWidget(resize?) {
    setTimeout(() => {
      if (resize) {
        const applyTargetSize = (
          propForDir: 'cols' | 'rows',
          newValue: number,
          push?: GridsterPush
        ) => {
          this.gridsterItem.$item[propForDir] = newValue;
          this.gridsterItem.item[propForDir] = newValue;
          if (push) {
            push.restoreItems();
            push.destroy();
          }
          this.gridsterItem.setSize();
        };

        const makePush = (propForDir: 'cols' | 'rows', oldValue: number, newValue: number) => {
          try {
            const push = new GridsterPush(this.gridsterItem);

            this.gridsterItem.item[propForDir] = oldValue;
            this.gridsterItem.$item[propForDir] = newValue;

            if (push.pushItems(propForDir === 'cols' ? push.fromWest : push.fromNorth)) {
              push.checkPushBack(); // check for items can restore to original position
              push.setPushedItems(); // save the items pushed
              this.gridsterItem.setSize();
              this.gridsterItem.checkItemChanges(this.gridsterItem.$item, this.gridsterItem.item);
              push.destroy();
            } else {
              applyTargetSize(propForDir, newValue, push);
            }
          } catch (e) {
            applyTargetSize(propForDir, newValue);
          }
        };

        if (this.gridsterItem.item.cols !== this.gridsterItem.$item.cols) {
          const oldValue = this.gridsterItem.$item.cols;
          const newValue = this.gridsterItem.item.cols;
          this.widget.cols = oldValue;

          this.dashboardViewComponent.dashboardOptions.draggable.enabled = true;
          this.dashboardViewComponent.dashboardOptions.api.optionsChanged();

          makePush('cols', oldValue, newValue);

          this.dashboardViewComponent.dashboardOptions.draggable.enabled = false;
          this.dashboardViewComponent.dashboardOptions.api.optionsChanged();

          // makePush sets up $item for push where widget.col gets temporarily set to oldValue.
          //  Need to Restore it to new value.
          this.widget.cols = newValue;
        }

        if (this.gridsterItem.item.rows !== this.gridsterItem.$item.rows) {
          const oldValue = this.gridsterItem.$item.rows;
          const newValue = this.gridsterItem.item.rows;
          this.widget.rows = oldValue;

          this.dashboardViewComponent.dashboardOptions.draggable.enabled = true;
          this.dashboardViewComponent.dashboardOptions.api.optionsChanged();

          makePush('rows', oldValue, newValue);

          this.dashboardViewComponent.dashboardOptions.draggable.enabled = false;
          this.dashboardViewComponent.dashboardOptions.api.optionsChanged();

          // restore widget.rows to the intended target value.
          this.widget.rows = newValue;
        }
      }

      this.elementComponent.redraw();
      if (this.showCommentList) {
        this.recalculateContainerWidth(this.commentListContainer);
      }

      if (this.showAiInsights) {
        this.recalculateContainerWidth(this.aiInsightsContainer);
      }
    });
  }

  onLoadingFinished($event) {
    this.onUpdateTitle();

    this.updateAiInsightsAction();

    this.widgetLoaded.emit($event);
  }

  onUpdateTitle() {
    this.defaultTitle = this.elementComponent?.defaultTitle ?? '';
  }

  scrollIntoView() {
    setTimeout(() => this.elementRef.nativeElement.scrollIntoView({ behavior: 'smooth' }));
  }

  async load() {
    await this.loadDataSource();

    // Load section metadata
    if (this.widgetDataSource?.hasSections) {
      await this.loadSectionMetadataList();
    }
  }

  async loadDataSource() {
    this.widgetDataSource = this.datasourceManagerStore.getById(this.widget.dataSourceId);
    this.datasourceManagerStore.registerDataSourceToWidget(
      this.widget.id,
      this.widget.dataSourceId
    );
    await this.getAnalyticItemsMap();
    if (!this.widgetDataSource) {
      if (this.elementComponent.needDataSource) {
        this.elementComponent.dataSourceNotFound = true;
      }
      return;
    }

    this.checkWeightFieldExists(this.widgetDataSource);
  }

  async loadSectionMetadataList() {
    try {
      this.sectionMetadata.push(
        ...(await this.sectionService.loadAllSectionMetadata(
          this.project.id,
          this.widgetDataSource
        ))
      );
    } catch (error) {
      this.logger.error('Failed to load section metadata list:', { error });
    }
  }

  updateWidgetGroupsExist() {
    this.hasWidgetGroups = this.widgetGroups.length > 0;
  }

  updateWidgetGroupsParentCheck() {
    this.isWidgetGroupParent = !!(this.widgetGroup?.parentWidget === this.widget.id);
  }

  private getComparisonErrorMessage(
    comparison?: Comparison,
    defaultErrorKey?: string
  ): string | undefined {
    const key = this.elementComponent?.getComparisonErrorMessage(comparison) ?? defaultErrorKey;
    if (key) {
      return translate(key);
    }
  }

  initWidgetActions() {
    // Ensure that action buttons shown properly
    let order = 1;

    this.widgetActions = {};

    if (!this.elementComponent || this.elementComponent.isLocked) {
      return;
    }

    if (this.canAddComment) {
      this.widgetActions.comments = {
        header: true,
        order: order++,
      };
      this.updateWidgetActionComments();
    }

    if (this.isInsightFeed()) {
      this.widgetActions.insightFeedsWidgetActions = {
        header: true,
        order: order++,
      };
    }

    if (this.isDocumentList()) {
      this.widgetActions.displaySettings = {
        header: true,
        order: order++,
      };
      this.widgetActions.displayDocumentTableView = {
        header: true,
        order: order++,
      };
      this.widgetActions.displayDocumentCardView = {
        header: true,
        order: order++,
      };
    }

    if (this.canWeigh()) {
      this.widgetActions.weight = {
        header: true,
        order: order++,
        disabled: this.elementComponent.weightDisabled,
        tooltip: this.elementComponent.weightTooltip,
      };
    }

    if (this.elementComponent.canFilter) {
      this.widgetActions.filter = {
        header: true,
        order: order++,
        disabled: this.elementComponent.filterDisabled,
      };
    }

    if (this.canHideItems()) {
      this.widgetActions.hiddenItems = {
        header: true,
        order: order++,
      };
    }

    if (this.canFilterSentiment()) {
      this.widgetActions.sentimentFilter = {
        header: true,
        order: order++,
        badge: FilterService.hasSentiemntFilterPolarityFilters(this.widget) ? '1' : '',
        disabled: !this.sentimentFilterEnabled(),
        tooltip: this.sentimentFilterEnabled()
          ? translate('widgets.sentiment-filter')
          : translate('widgets.no-feature-sentiment'),
      };
    }

    if (this.hasChartOptions()) {
      this.widgetActions.chartOptions = {
        header: true,
        order: order++,
      };
    }

    if (
      this.canChangeAnalyticItem() &&
      !this.isDocumentList() &&
      !this.isPredictive &&
      !this.isReadOnly
    ) {
      this.widgetActions.analyticItem = {
        header: true,
        order: order++,
      };
    }

    if (
      (this.widget.payload.limit || this.widget.payload.type === 'taxonomyMap') &&
      this.countSelectorsEnabled() &&
      !this.isDocumentList() &&
      !this.isNote &&
      !this.isPredictive &&
      !!this.contentLimitLabel()
    ) {
      this.widgetActions.contentLimit = {
        header: true,
        order: order++,
      };
    }

    if (this.hasAiInsightFeature && this.hasAiInsights) {
      this.widgetActions.aiInsights = {
        header: true,
        order: order++,
        tooltip: translate('widgets.ai-insights.tooltip'),
      };

      if (!this.isReadOnly) {
        this.widgetActions.aiInsights.disabled = true;
      }

      this.fetchMatchmakerScenes();
    }
  }

  private fetchMatchmakerScenes() {
    const fullContentChannel = this.datasourceManagerStore.getFullContentChannel(
      this.widgetDataSource?.contentChannel
    );
    this.aiInsightsOptions = {
      validatedScenes: [],
      dataPointCount: this.elementComponent.getAiInsightDataPointCount.bind(this.elementComponent),
      widget: this.widget,
      dashboard: this.dashboard,
      isReadOnly: this.isReadOnly,
      dataSource: this.widgetDataSource,
      widgetGroup: this.widgetGroup,
      contentChannel: fullContentChannel,
      canGenerateInsights: this.authService.canGenerateRevealInsights(),
      currentUserId: this.authService.currentUserId,
    };

    if (!this.hasAiInsightFeature || !this.hasAiInsights) {
      return;
    }

    if (!this.aiInsightsOptions.canGenerateInsights) {
      this.updateWidgetAction('aiInsights', { disabled: false });
      return;
    }

    this.updateWidgetAction('aiInsights', { disabled: true });

    const sceneParameters: MatchmakerProperties = {
      CONTAINERS: this.widgetAiInsightsService.mapWidgetTypeToContainer(this.widget),
    };
    if (fullContentChannel) {
      sceneParameters.CONTENT_CHANNELS = fullContentChannel;
    }

    this.widgetAiInsightsService.loadScenes(sceneParameters);
  }

  updateAiInsightsAction(handlers?: MatchmakerHandler[]) {
    if (!this.hasAiInsightFeature || !this.hasAiInsights) {
      return;
    }

    if (!this.authService.canGenerateRevealInsights()) {
      this.updateWidgetAction('aiInsights', {
        disabled: false,
        tooltip: translate('widgets.ai-insights.tooltip'),
      });
      return;
    }

    const validatedScenes = this.elementComponent.validateMatchmakerHandlers(handlers);

    const eligiblePoints = this.elementComponent.getAiInsightEligiblePointCount();

    if (eligiblePoints === 0) {
      this.aiInsightsOptions.validatedScenes = validatedScenes;
      this.updateWidgetAction('aiInsights', {
        disabled: true,
        tooltip: translate('widgets.ai-insights.doc-threshold.unavailable-below-min', {
          count: Constants.SMART_SUMMARY_MIN_DOC_COUNT_FOR_POINT,
        }),
      });
      return;
    }

    const adjustedScenes = validatedScenes.map((scene) => {
      if (scene.disabled) {
        return scene;
      }

      const clickablePoints = this.elementComponent.aiInsightValidatorService.getClickablePoints(
        scene.handler
      );
      const minRequired = clickablePoints.min;

      const hasEnoughEligiblePoints = this.elementComponent.hasEnoughAiInsightEligiblePoints(
        scene.handler,
        minRequired
      );

      if (!hasEnoughEligiblePoints) {
        return {
          ...scene,
          disabled: true,
          reason: translate('widgets.ai-insights.doc-threshold.only-points', {
            count: Constants.SMART_SUMMARY_MIN_DOC_COUNT_FOR_POINT,
            points: eligiblePoints,
          }),
        };
      }

      return scene;
    });

    if (adjustedScenes.every((s) => s.disabled)) {
      this.aiInsightsOptions.validatedScenes = adjustedScenes;
      this.updateWidgetAction('aiInsights', {
        disabled: true,
        tooltip: translate('widgets.ai-insights.no-scenes'),
      });
      return;
    }

    this.aiInsightsOptions.validatedScenes = adjustedScenes;

    this.updateWidgetAction('aiInsights', {
      disabled: false,
      tooltip: translate('widgets.ai-insights.tooltip'),
    });
  }

  updateWidgetAction(action: string, settings: Partial<WidgetActionSettings>) {
    if (!this.widgetActions[action]) {
      const maxOrder = Math.max(...Object.values(this.widgetActions).map((a) => a.order));
      this.widgetActions[action] = { header: settings.header, order: maxOrder + 1 };
      setTimeout(() => this.pillWrapper.checkCompactSize());
    }
    this.widgetActions[action] = { ...this.widgetActions[action], ...settings };
  }

  updateWidgetActionComments() {
    setTimeout(() => {
      const commentCount = this.widget.payload.comments?.length ?? 0;

      const visibleText = translate('widgets.comments.visible-comment-count', {
        count: this.visibleCommentsCount,
      });
      const hiddenText = translate('widgets.comments.hidden-comment-count', {
        count: commentCount - this.visibleCommentsCount,
      });

      this.updateWidgetAction('comments', {
        disabled: !commentCount,
        badge: commentCount ? '1' : '',
        tooltip: visibleText + '\n' + hiddenText,
      });
    });
  }

  private bringWidgetForthAndIncreaseSize(needsWidgetExtension = false): void {
    this.prepareWidgetForExpansion();

    const sizeChanged = this.adjustWidgetSize(needsWidgetExtension);

    if (sizeChanged) {
      this.redrawWidget(true);
    }
  }

  private prepareWidgetForExpansion(): void {
    this.ignoreWidgetChange.emit(true);
    this.widget.layerIndex = 2;
  }

  private adjustWidgetSize(needsWidgetExtension: boolean): boolean {
    let sizeChanged = false;

    if (needsWidgetExtension) {
      this.extendWidgetWidth();
      sizeChanged = true;
    }

    if (this.shouldExpandWidgetHeight()) {
      this.expandWidgetHeight();
      sizeChanged = true;
    }

    return sizeChanged;
  }

  private extendWidgetWidth(): void {
    const { maxCols } = this.dashboardOptions;
    const widgetPosX = this.originalWidget.x;
    const extendedWidthInCols =
      this.originalWidget.cols + WidgetComponent.EXPANSION_PANEL_WIDTH_COLS;

    if (widgetPosX + extendedWidthInCols > maxCols) {
      this.shiftWidgetAwayToFitExpansionPanel(maxCols);
    }

    this.widget.cols = this.originalWidget.cols + WidgetComponent.EXPANSION_PANEL_WIDTH_COLS;
  }

  private shouldExpandWidgetHeight(): boolean {
    const expandedRows = this.dashboardViewComponent.getExpandedRowsForWidget(this.widget);
    return expandedRows !== this.widget.rows;
  }

  private expandWidgetHeight(): void {
    const expandedRows = this.dashboardViewComponent.getExpandedRowsForWidget(this.widget);
    this.widget.rows = expandedRows;
  }

  private shiftWidgetAwayToFitExpansionPanel(maxCols: number): void {
    while (
      this.widget.x + (this.widget.cols + WidgetComponent.EXPANSION_PANEL_WIDTH_COLS) >
      maxCols
    ) {
      this.widget.x -= 1;
    }
  }

  private recalculateContainerWidth(container: ElementRef): void {
    if (container) {
      const expansionPanelWidth = this.getWidgetExpansionPanelWidth();
      container.nativeElement.style.width = `${expansionPanelWidth}px`;
    }
  }

  private getWidgetExpansionPanelWidth(): number {
    const itemComponent = this.dashboardOptions.api.getItemComponent(this.widget);
    const { margin } = itemComponent.gridster.$options;
    const { curColWidth } = itemComponent.gridster;
    return curColWidth * WidgetComponent.EXPANSION_PANEL_WIDTH_COLS - margin;
  }

  private bringWidgetBackAndResetSize(needRedraw = true): void {
    this.restoreWidgetSize();

    this.widget.layerIndex = 1;
    this.isWidgetDisabled = false;
    this.ignoreWidgetChange.emit(false);

    if (needRedraw) {
      this.redrawWidget(true);
    }
  }

  private restoreWidgetSize(): void {
    this.restoreWidgetWidth();
    this.restoreWidgetHeight();
  }

  private restoreWidgetWidth(): void {
    this.widget.cols = this.originalWidget.cols;
    if (this.widget.x !== this.originalWidget.x) {
      this.widget.x = this.originalWidget.x;
    }
  }

  private restoreWidgetHeight(): void {
    if (this.widget.rows !== this.originalWidget.rows) {
      this.widget.rows = this.originalWidget.rows;
    }
  }

  onCommentListOpenClicked(item) {
    this.beforeExpansionPanelOpen.emit(this.widget.id);
    this.commentsHandlerService.showExpansionPanel({
      widgetId: this.widget.id,
      widgetContainer: this.widgetComponent.widgetContainer,
      panelData: item,
      panelType: 'COMMENTS',
    });
  }

  openCommentList(item) {
    let pointComments = [];
    if (item) {
      pointComments = this.elementComponent.pointComments;
    }
    this.commentListModalOptions = {
      isDashboardLocked: this.dashboard.locked,
      smartSummaryIds: this.elementComponent.storedSmartSummaries?.map((ss) => ss.id) || [],
    };

    if (!this.showCommentList) {
      this.bringWidgetForthAndIncreaseSize(this.widgetNeedsOrHasExtension);

      setTimeout(() => {
        this.recalculateContainerWidth(this.commentListContainer);
        this.isWidgetDisabled = true;
      });

      this.showComments();
    }

    this.commentsHandlerService.setComments(
      this.elementComponent.getCommentsForPanel(),
      this.elementComponent.getVisibleComments()
    );
    this.commentsHandlerService.setCommentTarget(pointComments[0]?.target);
    this.commentsHandlerService.setCommentChanges(this.elementComponent.getCommentChanges());

    this.showCommentList = true;
  }

  onCommentListCloseClicked() {
    this.commentsHandlerService.hideExpansionPanel({
      widgetId: this.widget.id,
      panelType: 'COMMENTS',
    });
  }

  closeCommentList(): void {
    this.showCommentList = false;

    this.bringWidgetBackAndResetSize();
    this.hideOrShowAllComments(this.previousCommentVisibilityState);
    this.elementComponent.setAnnotationSelected(null);
  }

  hideComments() {
    this.previousCommentVisibilityState = true;
    this.hideOrShowAllComments(true);
  }

  showComments() {
    this.previousCommentVisibilityState = this.widget.payload.commentsHidden;
    this.hideOrShowAllComments(false);
  }

  private hideOrShowAllComments(hideComments: boolean): void {
    // after first load toggle hide and show stays on save trigger state instead of reverting to a non save state
    this.widget.payload.commentsHidden = hideComments;
    this.elementComponent.hideOrShowAllComments();
    this.updateWidgetActionComments();
    this.dashboardStore.markDirty();
  }

  private loadInsightFeeds(feedSource?: WidgetInsightFeedSource, feedQuery?: string[]) {
    this.aiInsightsGroupFeedService
      .getAiInsightsGroupFeeds(feedSource, this.dashboard.project_id, this.dashboard.id, feedQuery)
      .then((result) => {
        this.insightFeeds = result.items;
        this.setInsightFeedDropdownState(this.insightFeeds);
        this.insightFeedsortInsightToggle = false;
      });
  }

  private setInsightFeedDropdownState(feeds: AiInsightFeed[]): void {
    const { options, selectedValue } = this.aiInsightsGroupFeedService.getInsightFeedDropdownState(
      feeds,
      this.widget.payload.selectedInsightFeedValue
    );

    this.insightFeedSourceOptions = options;
    this.selectedInsightFeed = this.insightFeeds.find((feed) => feed.id === selectedValue);
    if (selectedValue !== this.widget.payload.selectedInsightFeedValue) {
      this.widget.payload.selectedInsightFeedValue = selectedValue;
      this.dashboardStore.markDirty();
    }
  }

  toggleInsightFeedSortOrder(): void {
    if (!this.isInsightFeed()) {
      return;
    }

    this.insightFeedsortInsightToggle = !this.insightFeedsortInsightToggle;
  }

  get insightFeedSortIcon(): string {
    return this.insightFeedsortInsightToggle
      ? 'kxi-icon-sort_order_asc'
      : 'kxi-icon-sort_order_desc';
  }

  get selectedInsightFeedValue(): string | undefined {
    return this.widget.payload.selectedInsightFeedValue;
  }

  onInsightFeedSourceChange(feedId: string) {
    this.selectedInsightFeed = this.insightFeeds.find((feed) => feed.id === feedId);
    this.widget.payload.selectedInsightFeedValue = feedId;
    this.dashboardStore.markDirty();
  }

  onInsightFeedSelectionChange(selectedInsights: AiInsight[] = []): void {
    this.selectedInsightFeedItems = selectedInsights;
  }

  onOpenCreateInsightFeedModal(action: 'create' | 'edit') {
    this.insightFeedModalOptions = {
      existingFeeds: this.insightFeeds,
      widget: this.widget,
      project: this.project,
      dashboard: this.dashboard,
      action: action,
    };
    this.showInsightFeedModal = true;
  }

  onCreateInsightFeedModalClosed(_feedCreated: boolean) {
    this.showInsightFeedModal = false;
  }

  async onFeedEvent($event: Pick<RealtimeMessage<AiInsightFeed>, 'verb' | 'object'>) {
    if (this.insightFeeds.some((feed) => feed.id === $event.object.id)) {
      if ($event.verb === 'UPDATE') {
        try {
          const sideloadedFeed = await this.aiInsightsGroupFeedService.getAiInsightsFeed(
            $event.object.id
          );
          const idx = this.insightFeeds.findIndex((feed) => feed.id === sideloadedFeed.id);
          this.insightFeeds[idx] = sideloadedFeed;

          if (this.selectedInsightFeedValue === sideloadedFeed.id) {
            this.selectedInsightFeed = sideloadedFeed;
          }
        } catch (error) {
          if (error?.status === 403) {
            const idx = this.insightFeeds.findIndex((feed) => feed.id === $event.object.id);
            if (idx !== -1) {
              this.insightFeeds.splice(idx, 1);
              if (this.selectedInsightFeedValue === $event.object.id) {
                let nextFeed = this.insightFeeds[idx];
                if (!nextFeed) {
                  nextFeed = this.insightFeeds[idx - 1];
                }
                this.onInsightFeedSourceChange(nextFeed?.id);
              }
            }
          }
        }
      }

      if ($event.verb === 'DELETE') {
        const idx = this.insightFeeds.findIndex((feed) => feed.id === $event.object.id);
        this.insightFeeds.splice(idx, 1);

        if (this.selectedInsightFeedValue === $event.object.id) {
          let nextFeed = this.insightFeeds[idx];
          if (!nextFeed) {
            nextFeed = this.insightFeeds[idx - 1];
          }
          this.onInsightFeedSourceChange(nextFeed?.id);
        }
      }
    }
    if ($event.verb === 'CREATE') {
      if (
        $event.object.projectId === this.project.id &&
        (this.widget.payload.feedSource === 'allProjectFeeds' ||
          $event.object.dashboardId === this.dashboard.id)
      ) {
        try {
          const sideloadedFeed = await this.aiInsightsGroupFeedService.getAiInsightsFeed(
            $event.object.id
          );
          this.insightFeeds.push(sideloadedFeed);
          if (!this.selectedInsightFeed) {
            this.onInsightFeedSourceChange(sideloadedFeed.id);
          }
        } catch (error) {
          if (error?.status !== 403) {
            throw error;
          }
        }
      }
    }
    this.insightFeeds = [...this.insightFeeds];
    this.insightFeedSourceOptions =
      this.aiInsightsGroupFeedService.getInsightFeedsDropdownObjectList(this.insightFeeds);
  }

  onCommentDeleted($event) {
    this.onCommentDialogClosed({ action: 'DELETE', comment: $event });
  }

  openSmartySummaryFromComment(summaryId: string) {
    this.elementComponent.openSmartSummaryById(summaryId);
  }

  toggleSelectedWidget(): void {
    if (!this.isWidgetSelectable()) {
      return;
    }
    this.selectModeStore.toggleWidget(this.widget);
  }

  get isSelected() {
    return this.selectModeStore.isWidgetSelected(this.widget.id);
  }

  updateCanBeParentOfFilterGroup() {
    const validWidgets = [
      'documentCount',
      'sentimentScore',
      'taxonomyMap',
      'wordcloud',
      'sentimentPolarity',
      'breakdown',
      'grid',
    ];
    this.canBeParentOfFilterGroup = validWidgets.includes(this.widget.payload.type);
    this.enabledToBeParentOfFilterGroup = true;
    if (
      this.widgetGroups.length === 1 &&
      ((this.dashboard.payload.type === 'predictive' && this.widgetGroups[0].isImpact) ||
        isDashboardLevelWidgetGroup(this.widgetGroups[0]))
    ) {
      this.enabledToBeParentOfFilterGroup = false;
    }
  }

  get id(): string {
    return this.widget.id;
  }

  getComponent(): AbstractWidgetComponent<any> {
    return this.elementComponent;
  }

  async downloadAsImage(): Promise<void> {
    this.document.body.style.cursor = 'progress';
    this.isImageExporting = true;
    setTimeout(() =>
      this.zone
        .runOutsideAngular(async () => {
          const format = this.isNewspaperShowing ? 'canvas' : 'blob';
          const widgetImage = await this.elementComponent.exportAsImage(format);
          if (!this.isNewspaperShowing) {
            return widgetImage as Blob;
          }

          const newspaperCanvas = await toCanvas(this.newspaperWidgletElement.nativeElement);

          const canvasResult = this.elementComponent.drawCanvasResult([
            widgetImage as HTMLCanvasElement,
            newspaperCanvas,
          ]);

          return firstValueFrom(
            this.httpClient.post<Blob>(canvasResult.dataUrl, canvasResult.body, {
              responseType: 'blob' as 'json',
            })
          );
        })
        .then((blob) => {
          this.systemService.saveFile(blob, 'widget.png');
          this.document.body.style.cursor = '';
          this.isImageExporting = false;
        })
    );
  }

  async downloadAsPptx(): Promise<void> {
    const pptx = this.pptxService.createPptx();
    await this.elementComponent.exportAsPptx(pptx);
    pptx.save(translate('widgets.widget'));
  }

  async downloadSelectedInsightsAsPptx(): Promise<void> {
    if (!this.selectedInsightFeedItems.length) {
      return;
    }
    const pptx = this.pptxService.createPptx();
    await this.elementComponent?.exportSelectedInsightsAsPptx(pptx, this.selectedInsightFeedItems);
    pptx.save(translate('widgets.widget-add.insight-feed'));
  }

  async downloadAsCsv(): Promise<void> {
    const csvContent = await this.elementComponent.exportAsCsv();
    if (csvContent) {
      this.systemService.saveFile(
        new Blob(['\ufeff' + csvContent.data], { type: 'text/plain;charset=utf-8' }),
        csvContent.fileName || 'Spotlight Export.csv'
      );
    }

    if (this.isNewspaperShowing) {
      const newspaperCsvContent = this.newspaperWidglet.exportAsCsv();
      if (newspaperCsvContent) {
        this.systemService.saveFile(
          new Blob(['\ufeff' + newspaperCsvContent.data], { type: 'text/plain;charset=utf-8' }),
          newspaperCsvContent.fileName || 'Spotlight Snapshot.csv'
        );
      }
    }
  }

  downloadAsExcel() {
    if (this.elementComponent) {
      (this.elementComponent as any).downloadAsExcel();
    }
  }

  downloadAsTable(): void {}

  async showAsDocumentList() {
    this.widget.properties.as_document_list = true;
    this.widget.properties.as_data_table = false;
    this.isShowAsDocumentList = true;
    this.isShowAsDataTable = false;
    await this.redrawWidget();
  }

  updateIsShowAsDocumentList() {
    this.isShowAsDocumentList = this.widget.properties.as_document_list;
  }

  showAsDataTable(): void {
    this.widget.properties.as_data_table = true;
    this.widget.properties.as_document_list = false;
    this.isShowAsDataTable = true;
    this.isShowAsDocumentList = false;
    this.widgetLoaded.emit(false);
  }

  async showAsGraph() {
    this.widget.properties.as_data_table = false;
    this.widget.properties.as_document_list = false;
    this.isShowAsDocumentList = false;
    this.isShowAsDataTable = false;
    await this.redrawWidget();
  }

  cloneClick(pageId?): void {
    this.cloneWidget.emit({ widget: this.widget, pageId });
  }

  moveClick(pageId): void {
    this.moveWidget.emit({ widget: this.widget, pageId });
  }

  deleteClick(): void {
    this.deleteWidget.emit(this.widget);
  }

  toggleTitleVisibility() {
    this.widget.payload.hideTitle = !this.widget.payload.hideTitle;
    this.dashboardStore.markDirty();
  }

  get widgetGroups() {
    return this.widgetGroupStore.getByPage(this.widget.payload.page);
  }

  isWidgetDirty(): boolean {
    if (!this.widget) {
      return false;
    }
    if (!this.elementComponent) {
      return false;
    }
    return this.elementComponent.isDirty;
  }

  async refreshWidget() {
    this.elementComponent.isDirty = false;
    if (this.widget && this.sentimentFilterEnabled() && this.widgetActions.sentimentFilter) {
      this.widgetActions.sentimentFilter.badge = FilterService.hasSentiemntFilterPolarityFilters(
        this.widget
      )
        ? '1'
        : '';
    }
    const dataSource = this.datasourceManagerStore.getById(this.widget.dataSourceId);
    this.widgetDataSource = dataSource;
    this.elementComponent.widgetDataSource = dataSource;
    this.widgetGroup = this.widgetGroupStore.getByWidget(this.widget.id);
    this.elementComponent.widgetGroup = this.widgetGroup;
    this.updateTemplateBooleans();
    if (this.widget && this.widget.payload.filters) {
      await this.applyWidgetFilter(
        this.widget.payload.filters,
        dataSource,
        undefined,
        this.widget.payload.sectionFilter
      );
    } else {
      await this.elementComponent.refresh(true, false, dataSource);
    }
  }

  async changeDataSource(dataSource: WidgetDataSource) {
    if (this.widget === undefined) {
      return;
    }
    this.elementComponent.dataSourceNotFound = false;
    this.elementComponent.isLoading = true;
    this.widgetDataSource = dataSource;
    await this.getAnalyticItemsMap();
    const prevDataSourceId = this.widget.dataSourceId;
    this.widget.dataSourceId = dataSource.id;
    this.elementComponent.widgetDataSource = dataSource;
    this.elementComponent.widgetDataSourceId = dataSource.id;

    delete this.widget.payload.chartMaximum;
    delete this.widget.payload.chartMaximumHorizontal;
    delete this.widget.payload.chartMaximumPercentage;
    delete this.widget.payload.showBlanks;
    delete this.widget.payload.chartMinimum;
    delete this.widget.payload.chartInterval;
    this.updateSelectedSections(dataSource);
    this.updateTemplateBooleans();

    this.checkWeightFieldExists(dataSource);
    this.updateWeightProperties(dataSource);

    this.getAndUpdateIfWidgetCanCreateKda();

    this.fetchMatchmakerScenes();

    this.elementComponent.refresh(true, false, dataSource, true);
    this.datasourceManagerStore.changeDataSource(this.widget.id, dataSource.id, prevDataSourceId);
    this.dashboardStore.markDirty();
  }

  private setDefaultSocialDateFilter(dataSource: WidgetDataSource) {
    if (dataSource?.isSocial) {
      this.widget.payload.filters =
        this.widget.payload.filters ?? FilterService.getDefaultFilterSet();
      const widgetDateFilters = this.widget.payload.filters.dates;

      if (
        !widgetDateFilters.and?.length &&
        !widgetDateFilters.not?.length &&
        !widgetDateFilters.or?.length
      ) {
        const today = new Date();
        today.setUTCHours(23, 59, 59, 999);

        const now = this.timezoneService.now();
        const toDate = now.format(DATE_TIME_WITHOUT_TIMEZONE_FORMAT);
        const fromDate = now.subtract(12, 'M').format(DATE_TIME_WITHOUT_TIMEZONE_FORMAT);

        this.translocoService.selectTranslateObject('widgets.options').subscribe((translations) => {
          widgetDateFilters.and = [
            {
              analyticItem: 'createdAt',
              displayTitle: translations['display-names.creation-date'],
              displayValue: translations['display.previous-six-months'],
              key: 'daterange',
              value: `daterange/${fromDate}/${toDate}`,
            },
          ];
        });
      }
    }
  }

  private updateSelectedSections(dataSource: WidgetDataSource) {
    if (this.widget === undefined) {
      return;
    }

    const getCompatibleSelectedSectionsFunction = (
      selectedSections: SelectedSection[] | SelectedSection | undefined,
      ds: WidgetDataSource
    ): SelectedSection[] | undefined => {
      if (!selectedSections || !ds?.hasSections) {
        return undefined;
      }
      const selectedSectionArray: SelectedSection[] = Array.isArray(selectedSections)
        ? selectedSections
        : [selectedSections];
      return selectedSectionArray;
    };
    const updateSelectedSectionsFunction = (payloadKey: string) => {
      const compatibleSelectedSection: SelectedSection[] | undefined =
        getCompatibleSelectedSectionsFunction(this.widget.payload[payloadKey], dataSource);
      if (compatibleSelectedSection) {
        this.widget.payload[payloadKey] = compatibleSelectedSection;
      } else {
        delete this.widget.payload[payloadKey];
      }
    };

    updateSelectedSectionsFunction('selectedSection');
    updateSelectedSectionsFunction('rowSelectedSections');
    updateSelectedSectionsFunction('columnSelectedSections');
  }

  private checkWeightFieldExists(dataSource: WidgetDataSource) {
    if (!dataSource || !this.widget?.payload.weightField) {
      return;
    }

    if (!dataSource.customFields?.includes(this.widget.payload.weightField)) {
      this.alertService.showToast(
        translate('widgets.weighting-removed', {
          fieldName: FieldService.getFieldLongName(this.widget.payload.weightField),
        }),
        undefined,
        undefined,
        false
      );
      delete this.widget.payload.weightField;
    }
  }

  isWidgetDatasourceRemoved(): boolean {
    if (!this.widget) {
      return false;
    }
    if (!this.elementComponent) {
      return false;
    }
    return (this.elementComponent as any).dataSourceNotFound;
  }

  areWidgetActionsDisabled(): boolean {
    if (!this.widget) {
      return false;
    }
    if (!this.elementComponent) {
      return false;
    }
    if (this.isCustomSortMode) {
      return true;
    }
    if (this.hasInvalidComparison) {
      return true;
    }
    return this.elementComponent.actionsDisabled();
  }

  updateIsWidgetDisabled() {
    this.isWidgetDisabled = this.isWidgetDatasourceRemoved() || this.areWidgetActionsDisabled();
  }

  updateCloneWidgetTooltip() {
    this.cloneWidgetTooltip = undefined;
    if (this.isWidgetDisabled && !this.canAddWidget) {
      this.cloneWidgetTooltip = translate('widgets.page-is-full');
    }
  }

  // BEGIN widget actions

  preventPropagation(popover: NgbPopover) {
    this.document.getElementById((popover as any)._ngbPopoverWindowId).onmousedown = (ev) => {
      ev.stopPropagation();
    };
  }

  onTitleChanged(title: string) {
    this.widget.title = title;
    this.widgetTitle = title;
    this.dashboardStore.markDirty();
  }

  openContentLimitPopover(popover: NgbPopover, $event?) {
    if (this.activePopover) {
      this.activePopover.close();
    }

    this.widgetContentLimit = this.widget.payload.limit || 10;

    popover.open({ $implicit: popover });
    if ($event) {
      $event.stopImmediatePropagation();
    }
    this.activePopover = popover;
  }

  closeContentLimitPopover(data: { $event: CustomEvent; popover: NgbPopover }) {
    const limit = data.$event.detail.value;
    this.closeWidgetMenu();
    this.widget.payload.limit = limit;
    data.popover.close();
    this.activePopover = undefined;
    this.updateTemplateBooleans();
    this.elementComponent.refresh();
    this.dashboardStore.markDirty();
  }

  disabledContentLimit() {
    return (
      this.isWidgetDisabled ||
      (this.widget.payload.selectedItems && this.widget.payload.selectedItems.length !== 0) ||
      (this.widget.payload.type === 'sentimentScore' &&
        this.widget.payload.analyticItem === 'documents')
    );
  }

  contentLimitLabel() {
    if (
      this.widget.payload.type === 'documentCount' &&
      this.widget.payload.analyticItem === 'documents'
    ) {
      return false;
    }
    if (this.widget.payload.type === 'taxonomyMap') {
      return this.widget.payload.limit || 10;
    }
    if (this.widget.payload.type === 'focusAreas' || this.widget.payload.type === 'insightFeed') {
      return false;
    }
    return (
      (this.widget.payload.selectedItems && this.widget.payload.selectedItems.length) ||
      this.widget.payload.limit
    );
  }

  get currentFilteredDocumentCount(): number {
    if (!this.elementComponent) {
      return 0;
    }

    return this.elementComponent.filteredDocumentCount ?? this.currentDocumentCount;
  }

  get currentDocumentCount(): number {
    if (this.widgetDataSource) {
      return this.widgetDataSource.unfilteredDocumentCount ?? this.widgetDataSource.documentCount;
    }
    return 0;
  }

  get filteredDocumentPercentage() {
    if (
      this.elementComponent.filteredDocumentCount === undefined &&
      this.elementComponent.widgetDataSource
    ) {
      return '…';
    }
    if (!this.currentDocumentCount) {
      return 0;
    }
    return ((this.currentFilteredDocumentCount / this.currentDocumentCount) * 100).toFixed(0);
  }

  async onCloseAddFilterToWidgetGroup(result) {
    this.showAddFilterToWidgetGroupModal = false;
    const groupId = result?.filterGroup.id;
    const zoomDateFilter = this.widget.payload.zoomDateFilter;
    if (groupId && zoomDateFilter) {
      if (this.dashboard.widgetGroups[groupId].widgets.find((id) => id === this.id)) {
        delete this.widget.payload.zoomDateFilter;

        const index = this.widget.payload.filters['dates']['and'].findIndex(
          (filt) => filt.analyticItem === 'createdAt' && filt.value === zoomDateFilter
        );
        this.widget.payload.filters['dates']['and'].splice(index, 1);
      }

      this.addDateFilterToWidgetGroup.emit({
        groupId,
        filter: zoomDateFilter,
      });
      this.dashboardStore.markDirty();
    }
  }

  resetZoom() {
    ChartDataService.resetZoom(this.widget);
    this.dashboardStore.markDirty();
    setTimeout(() => this.pillWrapper.checkCompactSize(), 10);
  }

  addFilterToWidgetGroup() {
    this.addFilterAvailableWidgetGroups = this.widgetGroups;
    this.showAddFilterToWidgetGroupModal = true;
  }

  async filterWidget(): Promise<void> {
    this.widgetDataSource = this.datasourceManagerStore.getById(this.widget.dataSourceId);
    await this.getAnalyticItemsMap();
    this.dialogService
      .showDialog<WidgetFilterDialogComponent, WidgetFilterDialogOptions>(
        WidgetFilterDialogComponent,
        {
          widget: this.widget,
          projectId: this.dashboard.project_id,
          analyticItem: this.widget.payload.analyticItem,
          sentimentScale: this.widget.payload.sentimentScale,
          filters: this.widget.payload.filters,
          userSearch: this.widget.payload.userSearch?.userSearch,
          widgetDataSource: this.widgetDataSource,
          widgetGroup: this.widgetGroup,
          forWidgetGroup: false,
          dashboard: this.dashboard,
          comparison: this.comparison,
        },
        {},
        false
      )
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          if (!result.multi) {
            this.applyWidgetFilter(result.filters, null, result.userSearch, result.sectionFilter);
          } else {
            this.selectModeStore.setSelectMode(
              'filters',
              {
                filters: result.filters,
                searchFilters: result.userSearch,
                sectionFilter: result.sectionFilter,
              },
              [this.widget]
            );
          }
          this.dashboardStore.markDirty();
        }
      });
  }

  async applyWidgetFilter(
    filters: AnalyticItemFilterSets,
    datasource?: WidgetDataSource,
    userSearch?: UserSearchFilter,
    sectionFilter?: SectionFilter[]
  ) {
    if (userSearch) {
      this.widget.payload.userSearch = userSearch;
    }

    this.widget.payload.sectionFilter = sectionFilter;

    if (filters) {
      // Preserve original sentiment-level filtering
      // see DASH-2248, DASH-2154
      if (this.canFilterSentiment() && this.sentimentFilterEnabled()) {
        const analyticItem = `analyses.${this.widget.payload.analyticItem}.sentimentPolarity${this.widget.payload.sentimentScale}`;
        let filterSet = this.widget.payload.filters[this.widget.payload.analyticItem] as FilterSet;
        const originalSentimentFilter = filterSet.or.filter((f) => f.analyticItem === analyticItem);

        this.widget.payload.filters = JSON.parse(JSON.stringify(filters));

        // Restore original sentiment-level filtering
        filterSet = this.widget.payload.filters[this.widget.payload.analyticItem] as FilterSet;
        filterSet.or = [
          ...filterSet.or.filter((f) => f.analyticItem !== analyticItem),
          ...originalSentimentFilter,
        ];
      } else {
        this.widget.payload.filters = JSON.parse(JSON.stringify(filters));
      }

      if (this.widget.payload.zoomDateFilter) {
        const and = filters.dates.and;
        const idx = and.findIndex((f) => f.value === this.widget.payload.zoomDateFilter);
        if (idx === -1) {
          delete this.widget.payload.zoomDateFilter;
        }
      }
    }

    this.updateTemplateBooleans();
    this.elementComponent.refresh(true, false, datasource);
    this.dashboardStore.markDirty();
  }

  setWeightField(field?): void {
    this.widget.payload.weightField = field?.fieldName;
    this.updateWeightProperties(this.widgetDataSource);
    this.elementComponent.refresh(true, false, this.widgetDataSource, false);
    this.dashboardStore.markDirty();
  }

  updateWeightProperties(widgetDataSource): void {
    this.elementComponent.updateWeightingProperties(widgetDataSource);

    if (this.widgetActions.weight) {
      this.widgetActions.weight.disabled = this.elementComponent?.weightDisabled;
      this.widgetActions.weight.tooltip = this.elementComponent.weightTooltip;
    }
  }

  openHiddenItemsPopover(popover: NgbPopover, $event?) {
    if (this.activePopover) {
      this.activePopover.close();
    }

    popover.open({ $implicit: popover });
    if ($event) {
      $event.stopImmediatePropagation();
    }

    this.activePopover = popover;
  }

  get disabledWidgetHiddenItems(): boolean {
    if (this.isWidgetDisabled) {
      return true;
    }
    return this.excludedValues.length === 0;
  }

  removeHiddenItemByIdx(idx: number) {
    if (idx !== -1) {
      this.excludedValues.splice(idx, 1);
    } else {
      this.excludedValues.splice(0, this.excludedValues.length);
    }
    this.elementComponent.refresh();
    this.dashboardStore.markDirty();
  }

  removeHiddenItem(item: any, popover: NgbPopover) {
    this.closeWidgetMenu();

    const idx = this.excludedValues.findIndex((i) => {
      // Backwards compatibility
      if (typeof item === 'string') {
        return i === item;
      }
      return i.name === item.name && i.type === item.type && i.subType === item.subType;
    });
    if (idx !== -1) {
      this.excludedValues.splice(idx, 1);

      this.predictiveWidgetDataService.changeSpecialImpactUnhide({
        id: this.widget.id,
        idx,
      });
    }
    popover.close();
    this.elementComponent.refresh();
    this.dashboardStore.markDirty();
  }

  removeAllHiddenItems(popover: NgbPopover) {
    this.closeWidgetMenu();

    this.excludedValues.splice(0, this.excludedValues.length);

    this.predictiveWidgetDataService.changeSpecialImpactUnhide({
      id: this.widget.id,
      idx: -1,
    });

    popover.close();
    this.elementComponent.refresh();
    this.dashboardStore.markDirty();
  }

  onOpenWidgetChartOptionsModal() {
    if (this.activePopover) {
      this.activePopover.close();
    }

    const getNumericAxisLabelsValue = () => {
      if (Object.getOwnPropertyNames(this.widget.payload).includes('showNumericAxisLabels')) {
        return this.widget.payload.showNumericAxisLabels;
      }
      return false;
    };
    this.chartOptionsData = {
      payload: JSON.parse(
        JSON.stringify({
          ...this.widget.payload,
          ...(this.isPredictive && { showNumericAxisLabels: getNumericAxisLabelsValue() }),
        })
      ),
      needRefresh: false,
      widgetDataSource: this.widgetDataSource,
      elementComponent: this.elementComponent,
      widgetGroup: this.widgetGroup,
      comparison: this.comparison,
      defaultColorPalette: (this.project.colorPalette ?? DEFAULT_PROJECT_COLOR_PALETTE).map(
        (colorString) => ChartCustomColorService.colorStringToRgbaColor(colorString)
      ),
      sentimentScale: this.dashboard.payload.sentimentScale,
    };
    this.showWidgetChartOptionsModal = true;
  }

  onOpenChangeWidgetGroupModal(adding) {
    if (this.isWidgetReadOnly) {
      return;
    }
    if (this.activePopover) {
      this.activePopover.close();
    }
    this.addingToWidgetGroup = adding;
    this.showWidgetWidgetGroupModal = true;
  }

  onOpenPromoteWidgetGroupModal() {
    if (this.isWidgetReadOnly) {
      return;
    }
    if (this.activePopover) {
      this.activePopover.close();
    }
    this.adjustingFilterGroupParent = true;
    this.showWidgetWidgetGroupModal = true;
  }

  onDemoteFromWidgetGroup() {
    if (this.isWidgetReadOnly) {
      return;
    }
    if (this.widgetGroup) {
      this.alertService.showToast(
        translate('widgets.widget-group-parent-removed', { widgetGroup: this.widgetGroup.title })
      );
      this.updateWidgetGroupParent.emit({
        groupId: this.widgetGroup.id,
        widgetId: this.widget.id,
        action: 'demote',
      });
    }
  }

  onRemoveFromWidgetGroup() {
    if (this.isWidgetReadOnly) {
      return;
    }
    if (this.widgetGroup) {
      this.alertService.showToast(
        translate('widgets.widget-group-removed', { widgetGroup: this.widgetGroup.title })
      );
      if (this.widgetGroup.parentWidget && this.widgetGroup.parentWidget === this.widget.id) {
        this.updateWidgetGroupParent.emit({
          groupId: this.widgetGroup.id,
          widgetId: this.widget.id,
          action: 'remove',
        });
      } else {
        this.updateWidgetGroupMembership.emit({
          groupId: this.widgetGroup.id,
          widgetId: this.widget.id,
          action: 'remove',
        });
      }
    }
  }

  onWidgetWidgetGroupModalClosed(result) {
    this.showWidgetWidgetGroupModal = false;

    if (result) {
      const groupToAddTo = Object.getOwnPropertyNames(result).includes('addToGroup')
        ? result['addToGroup']
        : undefined;
      const groupToRemoveFrom = Object.getOwnPropertyNames(result).includes('removeFromGroup')
        ? result['removeFromGroup']
        : undefined;
      if (groupToAddTo !== groupToRemoveFrom || this.adjustingFilterGroupParent) {
        if (groupToRemoveFrom) {
          let skipRefresh = false;
          if (groupToAddTo) {
            skipRefresh = true;
          }
          if (this.adjustingFilterGroupParent) {
            this.updateWidgetGroupParent.emit({
              groupId: groupToRemoveFrom,
              widgetId: this.widget.id,
              action: 'remove',
              skipRefresh,
            });
          } else {
            this.updateWidgetGroupMembership.emit({
              groupId: groupToRemoveFrom,
              widgetId: this.widget.id,
              action: 'remove',
              skipRefresh,
            });
          }
        }
        if (groupToAddTo) {
          if (this.adjustingFilterGroupParent) {
            this.updateWidgetGroupParent.emit({
              groupId: groupToAddTo,
              widgetId: this.widget.id,
              action: 'add',
            });
          } else {
            this.updateWidgetGroupMembership.emit({
              groupId: groupToAddTo,
              widgetId: this.widget.id,
              action: 'add',
            });
          }
        }
      }
    }

    this.addingToWidgetGroup = false;
    this.adjustingFilterGroupParent = false;
  }

  onRemoveFromComparison() {
    if (this.isWidgetReadOnly) {
      return;
    }
    if (this.comparison) {
      this.alertService.showToast(
        translate('widgets.widget-comparison-removed', { comparison: this.comparison.title })
      );

      this.comparisonService.revokeComparison(this.comparison, this.widget);
    }
  }

  onWidgetChartOptionsModalClosed(data?: ChartDisplayOptions) {
    if (data) {
      //If the user selected a timerange then force that timerange until updates.
      if (data.payload.dateInterval !== this.widget.payload.dateInterval) {
        data.payload.userSelectedDateRange = true;
      }

      Object.assign(this.widget.payload, data.payload);

      const parseToNumberIfDefined = (value): number | undefined => {
        if (value == null || value === '') {
          return undefined;
        }

        return +value;
      };

      this.widget.payload.chartMaximum = parseToNumberIfDefined(this.widget.payload.chartMaximum);
      this.widget.payload.chartMaximumHorizontal = parseToNumberIfDefined(
        this.widget.payload.chartMaximumHorizontal
      );
      this.widget.payload.chartMaximumPercentage = parseToNumberIfDefined(
        this.widget.payload.chartMaximumPercentage
      );
      this.widget.payload.chartGoal = parseToNumberIfDefined(this.widget.payload.chartGoal);
      this.widget.payload.chartMinimum = parseToNumberIfDefined(this.widget.payload.chartMinimum);
      this.widget.payload.chartInterval = parseToNumberIfDefined(this.widget.payload.chartInterval);

      this.elementComponent.handleChartOptionsChanging();
      const shouldRefreshWidgetData =
        this.elementComponent.shouldRefreshAfterChartOptionsChange(data);

      if (shouldRefreshWidgetData) {
        this.updateAiInsightsAction();

        if (this.isPredictive && this.widgetGroup?.parentWidget) {
          this.dashboardViewComponent.refreshWidgets([
            this.widgetGroup.parentWidget,
            ...this.widgetGroup.widgets,
          ]);
        } else {
          this.updateTemplateBooleans();
          this.elementComponent.refresh(this.widget.payload.type === 'bigNumber');
        }
      }
      this.dashboardStore.markDirty();
    }
    this.showWidgetChartOptionsModal = false;
  }

  openWidgetSentimentFilter() {
    this.dialogService
      .showDialog<WidgetSentimentFilterDialogComponent, WidgetSentimentFilterDialogOptions>(
        WidgetSentimentFilterDialogComponent,
        {
          widget: this.widget,
        },
        {},
        false
      )
      .afterClosed()
      .subscribe((result) => {
        if (result && result.polarities) {
          const sentimentFilterPolarities = result.polarities as SentimentFilterPolarity[];
          const analyticItem = `analyses.${this.widget.payload.analyticItem}.sentimentPolarity${this.widget.payload.sentimentScale}`;
          const filterSet = this.widget.payload.filters[
            this.widget.payload.analyticItem
          ] as FilterSet;
          let hasCustomizations = false;
          const checkedCount = sentimentFilterPolarities.filter((f) => f.checked).length;
          switch (this.widget.payload.sentimentScale) {
            case 5:
              hasCustomizations = checkedCount !== 5;
              break;
            default:
              hasCustomizations = checkedCount !== 3;
          }

          if (hasCustomizations) {
            for (const polarity of sentimentFilterPolarities) {
              FilterService.setAnalayticFeaturePolarityFilter(
                filterSet,
                analyticItem,
                polarity.value,
                polarity.checked
              );
            }
          } else {
            FilterService.clearFeatureSentimentFilters(filterSet, analyticItem);
          }
          if (this.widgetActions.sentimentFilter) {
            this.widgetActions.sentimentFilter.badge = hasCustomizations ? '1' : '';
          }
          this.updateTemplateBooleans();
          this.elementComponent.refresh();
          this.dashboardStore.markDirty();
        }
      });
  }

  isDisplayedOverTimeEnabled(): boolean {
    let enabled = true;
    if (!this.widgetDataSource.hasDateData) {
      enabled = false;
    }
    if (!this.currentReport.options.widgetDataSource.hasDateData) {
      enabled = false;
    }
    if (this.widget.payload.isDrilledDown) {
      enabled = false;
    }
    return enabled;
  }

  widgetAnalyticTypeTooltip(): string {
    if (this.widget.payload.type === 'bigNumber' && this.analyticItemChangeDisabled()) {
      return translate('widgets.datasource-no-numeric-custom-fields');
    }
    if (this.hasCustomSet() && this.widget.payload.type !== 'sentimentVolume') {
      return translate('widgets.set-analytic-items');
    }
    return translate('widgets.set-analytic-item');
  }

  dataSourceChangeDisabled() {
    return (
      this.dashboard.locked ||
      this.hasInvalidComparison ||
      (this.elementComponent?.isDataSourceChangeDisabled() ?? true)
    );
  }

  analyticItemChangeDisabled() {
    return this.elementComponent?.isAnalyticItemChangeDisabled() ?? true;
  }

  canChangeAnalyticItem() {
    return this.elementComponent?.canChangeAnalyticItem();
  }

  widgetGroupChangeDisabled() {
    return this.elementComponent?.isWidgetGroupChangeDisabled() ?? true;
  }

  canAddToWidgetGroup() {
    return !!this.elementComponent?.canAddToWidgetGroup();
  }

  canAddToComparison() {
    return this.elementComponent?.canAddToComparison();
  }

  canApplyComparison(comparison?: Comparison) {
    if (
      (!this.hasInvalidComparison && this.isWidgetDisabled) ||
      !this.comparisonList?.length ||
      !this.elementComponent?.canApplyComparison(comparison)
    ) {
      return false;
    }

    if (!comparison) {
      return !this.comparison;
    }

    const checkForAnalyticItem = (fi: Filter) => {
      if (FilterUtils.isComparisonSectionFilter(fi)) {
        return (
          Constants.SECTION_PROPERTY_COMPARISON_ALLOWED_WIDGET_TYPES.includes(
            this.widget.payload.type
          ) && this.sectionMetadata?.some((f) => f.fieldName === fi.analyticItem)
        );
      }
      const analyticItem = fi.analyticItem.split('.')[1];
      if (analyticItem) {
        return this.analyticItemTypes?.metadata?.subTypes?.includes(analyticItem);
      }
      // analyticItem should be 'createdAt' here;
      return this.widgetDataSource.hasDateData;
    };

    const checkComparisonFilterMatch = (compFiltermatch) => {
      for (const matchFilter of compFiltermatch.filters) {
        for (const op of ['and', 'or', 'not']) {
          for (const f of matchFilter[op]) {
            const matches = checkForAnalyticItem(f);
            if (!matches) {
              return false;
            }
          }
        }
      }

      return true;
    };

    return checkComparisonFilterMatch(comparison);
  }

  applyComparisonTooltip(comparison?) {
    return this.getComparisonErrorMessage(comparison);
  }

  comparisonChangeDisabled() {
    if (this.hasInvalidComparison) {
      return false;
    }
    return this.elementComponent?.isComparisonChangeDisabled() ?? true;
  }

  calculateShowWidgetMenu(): boolean {
    return (
      Object.values(this.widgetActions).some((a) => a.header === false) ||
      this.canBeParentOfFilterGroup ||
      this.canShowDataTable ||
      this.canShowDocumentList ||
      this.canDownload ||
      this.widgetGroupChangeDisabled() ||
      this.canCreateCollection ||
      this.canBeCloned ||
      this.canBeDeleted
    );
  }

  async openAnalyticTypeDialog(): Promise<boolean> {
    if (
      this.widget.payload.type === 'featureScoring' ||
      this.widget.payload.type === 'sentimentAccuracy' ||
      (this.widget.payload.type === 'numericMetadata' &&
        this.widget.component !== 'DashboardWidgetJourneyComponent') ||
      this.widget.payload.type === 'breakdown' ||
      this.widget.payload.type === 'donut' ||
      this.widget.payload.type === 'gauge' ||
      this.hasCustomSet()
    ) {
      this.addWidget.emit(this.widget);
      return false;
    }

    this.currentReport = this.getReport();

    const metadataFields = this.analyticItemTypes?.metadata?.subTypes;

    this.analyticItemTypes.entities.subTypes.sort();
    if (this.widget.payload.type === 'crosstab') {
      this.crosstabAnalyticItemModalOptions = {
        widget: this.widget,
        widgetDataSource: this.widgetDataSource,
        analyticItemTypes: this.analyticItemTypes,
        report: this.currentReport,
      };
      this.showCrosstabAnalyticItemsModal = true;
    } else if (this.widget.payload.type === 'bigNumber') {
      this.dialogService
        .showDialog<BigNumberMetadataSelectorDialogComponent, BigNumberMetadataDialogOptions>(
          BigNumberMetadataSelectorDialogComponent,
          {
            widget: this.widget,
            widgetDataSource: this.widgetDataSource,
            newWidget: false,
            metadataFields,
          },
          {},
          false
        )
        .afterClosed()
        .subscribe((result: BigNumberMetadataDialogResult | null | ModalCloseType) => {
          if (result && result !== ModalCloseType.CANCEL && result !== ModalCloseType.CLOSE) {
            this.setAnalyticTypeBigNumber(result);
          }
        });
    } else if (this.widget.payload.type === 'grid') {
      this.gridAnalyticItemSelectorDialogOptions = {
        widget: this.widget,
        widgetDataSource: this.widgetDataSource,
        isNew: false,
        metadataFields,
        sentimentScale: this.dashboard.payload.sentimentScale,
        comparison: this.comparison,
      };

      this.showGridAnalyticItemSelectorDialog = true;
    } else if (this.widget.component === 'DashboardWidgetJourneyComponent') {
      this.journeyAnalyticItemSelectorDialogOptions = {
        widget: this.widget,
        widgetDataSource: this.widgetDataSource,
        isNew: false,
        metadataFields,
        sentimentScale: this.dashboard.payload.sentimentScale,
      };

      this.showJourneyAnalyticItemSelectorDialog = true;
    } else {
      // Monkey-patch around DASH-2976
      if (this.widget.payload.cloudFromTaxonomy && !this.widget.payload.entityTypeFilter) {
        this.widget.payload.entityTypeFilter = AnalyticItemTypes.defaultTopicTypes.join();
      }

      this.analyticItemModalOptions = {
        widget: this.widget,
        widgetDataSource: this.widgetDataSource,
        analyticItemTypes: this.analyticItemTypes,
        report: this.currentReport,
      };
      this.showAnalyticItemModal = true;
    }

    return true;
  }

  onCloseGridAnalyticItemSelectorDialog(
    result: GridAnalyticItemSelectorDialogResult | null | ModalCloseType
  ): void {
    if (result && result !== ModalCloseType.CANCEL && result !== ModalCloseType.CLOSE) {
      setGridAnalyticItemSelectorDialogResultOnPayload(this.widget.payload, result);
      this.dashboardStore.markDirty();

      this.elementComponent.refresh().then(() =>
        setTimeout(() => {
          this.updateTemplateBooleans();
          this.initWidgetActions();
          this.pillWrapper.checkCompactSize();
        })
      );
    }

    this.showGridAnalyticItemSelectorDialog = false;
  }

  onCloseJourneyAnalyticItemSelectorDialog(
    result: JourneyAnalyticItemSelectorDialogResult | null | ModalCloseType
  ): void {
    if (result && result !== ModalCloseType.CANCEL && result !== ModalCloseType.CLOSE) {
      this.widget.payload.numericMetadataFields = result.numericMetadataFields;
      this.dashboardStore.markDirty();

      this.elementComponent.refresh(true, false).then(() =>
        setTimeout(() => {
          this.updateTemplateBooleans();
          this.initWidgetActions();
          this.pillWrapper.checkCompactSize();
        })
      );
    }

    this.showJourneyAnalyticItemSelectorDialog = false;
  }

  onCloseAnalyticItemModal(result: WidgetAnalyticItemDialogResult) {
    this.showAnalyticItemModal = false;
    if (result) {
      this.setAnalyticType(result);
      this.dashboardStore.markDirty();
    }
  }

  onCloseCrosstabAnalyticItemsModal(result) {
    this.showCrosstabAnalyticItemsModal = false;
    if (result) {
      this.setAnalyticTypeCrosstab(result);
      this.dashboardStore.markDirty();
    }
  }

  setAnalyticTypeCrosstab(result) {
    if (
      this.widget.payload.analyticItem === result.row.analyticItem &&
      this.widget.payload.columnAnalyticItem === result.column.analyticItem
    ) {
      //The currently selected entity type.
      if (
        this.widget.payload.entityTypeFilter === result.row.subTypes &&
        this.widget.payload.columnEntityTypeFilter === result.column.subTypes &&
        Utils.deepEquals(this.widget.payload.rowSelectedSections, result.row.selectedSections) &&
        Utils.deepEquals(this.widget.payload.columnSelectedSections, result.column.selectedSections)
      ) {
        return;
      }
    }

    this.widget.payload.customFieldSelected = result.row.customField || false;
    this.widget.payload.standardFieldSelected = result.row.standardField || false;
    this.widget.payload.sectionPropertySelected = result.row.sectionProperty || false;
    this.widget.payload.rowSelectedSections = result.row.selectedSections;
    this.widget.payload.columnCustomFieldSelected = result.column.customField || false;
    this.widget.payload.columnStandardFieldSelected = result.column.standardField || false;
    this.widget.payload.columnSectionPropertySelected = result.column.sectionProperty || false;
    this.widget.payload.columnSelectedSections = result.column.selectedSections;

    const fillItemProps = (resultProp: 'row' | 'column') => {
      const payloadItemProp = resultProp === 'row' ? 'analyticItem' : 'columnAnalyticItem';
      const payloadSubTypeProp =
        resultProp === 'row' ? 'entityTypeFilter' : 'columnEntityTypeFilter';

      if (
        result[resultProp].analyticItem !== 'metadata' &&
        result[resultProp].analyticItem !== 'standard Fields' &&
        !result[resultProp].analyticItem?.startsWith('sectionProperties.')
      ) {
        this.widget.payload[payloadItemProp] = result[resultProp].analyticItem;
        this.widget.payload[payloadSubTypeProp] = result[resultProp].subTypes;
      }

      if (result[resultProp].analyticItem === 'metadata' && result[resultProp].subtypes) {
        this.widget.payload[payloadItemProp] = result[resultProp].subtypes;
      }

      if (result[resultProp].analyticItem === 'standard Fields' && result[resultProp].subtypes) {
        this.widget.payload[payloadItemProp] = result[resultProp].subtypes;
      }

      if (result[resultProp].analyticItem?.startsWith('sectionProperties.')) {
        this.widget.payload[payloadItemProp] = result[resultProp].analyticItem;
        this.widget.payload[payloadSubTypeProp] = '';
      }
    };

    fillItemProps('row');
    fillItemProps('column');
    this.currentReport = this.getReport();
    // reset hidden items
    if (this.excludedValues.length > 0) {
      this.excludedValues.splice(0, this.excludedValues.length);
    }

    this.elementComponent.refresh().then(() =>
      setTimeout(() => {
        this.updateTemplateBooleans();
        this.initWidgetActions();
        this.pillWrapper.checkCompactSize();
      })
    );
  }

  private setAnalyticTypeBigNumber(result: BigNumberMetadataDialogResult) {
    //check to see if changes first

    this.widget.payload.entityTypeFilter = '';

    this.widget.payload.analyticItem = result.fieldOne.analyticItem;
    this.widget.payload.metadataDisplayNameOne = result.fieldOne.metadataItemDisplayName;
    this.widget.payload.useFieldDisplayNameOne = result.fieldOne.useFieldDisplayName;
    this.widget.payload.customFieldSelected = result.fieldOne.customField;

    this.widget.payload.analyticItemTwo = result.fieldTwo.analyticItem;
    this.widget.payload.metadataDisplayNameTwo = result.fieldTwo.metadataItemDisplayName;
    this.widget.payload.useFieldDisplayNameTwo = result.fieldTwo.useFieldDisplayName;
    this.widget.payload.metadataTwoCustomFieldSelected = result.fieldTwo.customField;

    this.currentReport = this.getReport();

    // reset hidden items
    if (this.excludedValues.length > 0) {
      this.excludedValues.splice(0, this.excludedValues.length);
    }
    this.dashboardStore.markDirty();

    this.elementComponent.refresh().then(() =>
      setTimeout(() => {
        this.updateTemplateBooleans();
        this.initWidgetActions();
        this.pillWrapper.checkCompactSize();
      })
    );
  }

  setAnalyticType(result: WidgetAnalyticItemDialogResult) {
    this.elementComponent.analyticTypeSet(result);
    const needsRedraw = this.setAnalyticTypeResult(result);

    if (needsRedraw || result.showBlanksChanged) {
      this.elementComponent.refresh().then(() => {
        this.updateTemplateBooleans();
        this.initWidgetActions();

        setTimeout(() => {
          this.pillWrapper.checkCompactSize();
        });
      });
    }
  }

  setAnalyticTypeResult(result: WidgetAnalyticItemDialogResult): boolean {
    const analyticItem = result.analyticItem;
    const subtypes = result.subtypes;

    //Prevent anything from happening on:
    //The custom and standard field label
    if ((analyticItem === 'metadata' || analyticItem === 'standard Fields') && !subtypes.length) {
      return false;
    }

    //The currently selected custom or standard field
    if (
      (analyticItem === 'metadata' || analyticItem === 'standard Fields') &&
      this.widget.payload.analyticItem === subtypes
    ) {
      return false;
    }

    if (
      this.widget.payload.analyticItem === analyticItem &&
      Utils.deepEquals(this.widget.payload.selectedSection, result.selectedSection)
    ) {
      //The currently selected subtype.
      if (this.widget.payload.entityTypeFilter === subtypes) {
        return false;
      }
    }

    if (this.widget.component !== 'DashboardWidgetImpactComponent') {
      delete this.widget.payload.sort;
    }

    if (this.canFilterSentiment()) {
      //Clear out any feature sentiment filters
      let sentimentValues = ['positive', 'neutral', 'negative'];
      if (this.widget.payload.sentimentScale === 5) {
        sentimentValues = [
          'very positive',
          'somewhat positive',
          'neutral',
          'very negative',
          'somewhat negative',
        ];
      }
      const filterSet = this.widget.payload.filters[this.widget.payload.analyticItem] as FilterSet;
      const currentPolarityFilter = `analyses.${this.widget.payload.analyticItem}.sentimentPolarity${this.widget.payload.sentimentScale}`;

      //Handle feature sentiment polarity filters
      //Don't bother to do this if we're just changing subtype
      if (analyticItem !== this.widget.payload.analyticItem) {
        const addBack = [];
        //Remove the feature setiment filters of the previously selected analytic item
        sentimentValues.forEach((value) => {
          if (filterSet && filterSet.or) {
            const idx = filterSet.or.findIndex(
              (f) => f.analyticItem === currentPolarityFilter && f.value === value
            );
            if (idx !== -1) {
              addBack.push(value);
            }
          }
        });
        FilterService.clearFeatureSentimentFilters(filterSet, currentPolarityFilter);
        if (this.canFilterSentiment() && this.sentimentFilterEnabled()) {
          this.widgetActions.sentimentFilter.badge = '';
        }

        //Add back the previous sentiment filters with the new analytic item
        //Just remove filters completely for documents, custom, and standard fields as they have no feature sentiment
        const doNotAddedBack = ['documents', 'metadata', 'standard Fields'];
        if (!doNotAddedBack.includes(analyticItem)) {
          const newPolarityFilter = `analyses.${analyticItem}.sentimentPolarity${this.widget.payload.sentimentScale}`;
          const newfilterSet = this.widget.payload.filters[analyticItem] as FilterSet;
          addBack.forEach((add) => {
            FilterService.setAnalayticFeaturePolarityFilter(
              newfilterSet,
              newPolarityFilter,
              add,
              true
            );
            if (this.canFilterSentiment() && this.sentimentFilterEnabled()) {
              this.widgetActions.sentimentFilter.badge = addBack.length > 0 ? '1' : '';
            }
          });
        }
      }
    }

    this.widget.payload.analyticItem = analyticItem;
    this.widget.payload.entityTypeFilter = subtypes;
    this.widget.payload.customFieldSelected = result.customField;
    this.widget.payload.standardFieldSelected = result.standardField;
    this.widget.payload.sectionPropertySelected = result.sectionProperty;
    this.widget.payload.selectedSection = result.selectedSection;

    this.currentReport = this.getReport();
    //Set the proper graph types for by documents and set back to a default.
    if (
      this.widget.payload.graphType &&
      this.currentReport.getGraphTypes().indexOf(this.widget.payload.graphType) === -1
    ) {
      const originalGraphType =
        (<WidgetChartComponent>this.elementComponent).originalGraphType ||
        this.currentReport.getGraphTypes()[0];
      this.elementComponent.payload.graphType = originalGraphType;
      this.widget.payload.graphType = originalGraphType;
    }

    if (this.widget.payload.aggregator) {
      const aggs = this.currentReport.getAggregators();
      if (aggs.indexOf(this.widget.payload.aggregator) === -1) {
        this.widget.payload.aggregator = aggs[0];
      }

      if (
        this.currentReport.hasTimeInterval() &&
        ChartOptionUtils.isOverTimeAggregator(this.widget.payload.aggregator)
      ) {
        const chartComponent = this.elementComponent as WidgetChartComponent;
        if (!chartComponent.isDateIntervalEnabled(this.widget.payload.dateInterval)) {
          this.widget.payload.dateInterval = chartComponent.minDateInterval;
        }
      } else if (this.widget.payload.dateInterval) {
        this.widget.payload.dateInterval = undefined;
      }
    }

    if (!this.currentReport.canSetChartGoal()) {
      delete this.widget.payload.chartGoal;
    }

    this.widget.payload.drilledDownOn = 'none';
    this.widget.payload.isDrilledDown = false;

    // reset hidden items
    if (this.excludedValues.length > 0) {
      this.excludedValues.splice(0, this.excludedValues.length);
    }

    if (this.widget.payload.legends) {
      this.widget.payload.legends = {};
    }

    return true;
  }

  openDocumentListSettings() {
    const documentListWidget = this.elementComponent as WidgetDocumentListComponent;
    const isFieldDisplayedFn = (fieldList) => (fieldId) =>
      fieldList?.find((f) => f.id === fieldId)?.display ?? true;
    const isNlpFieldDisplayed = isFieldDisplayedFn(
      documentListWidget.payload.documentViewer?.nlpFields
    );
    const sortFields = (fieldList, availableFields) => {
      if (fieldList) {
        availableFields.sort(
          (f1, f2) =>
            fieldList.findIndex((f) => f.id === f1.id) - fieldList.findIndex((f) => f.id === f2.id)
        );
      }
    };

    const textFields = documentListWidget.textFields
      .filter((field) => field.available)
      .map((field) => ({ ...field }));
    const otherFields = documentListWidget.otherFields
      .filter((field) => field.available || field.property === 'tags')
      .map((field) => ({ ...field }));

    const availableNlpFields = [
      {
        id: 'analyses.entities.title',
        title: translate('widgets.entities'),
        display: isNlpFieldDisplayed('analyses.entities.title'),
      },
      {
        id: 'analyses.phrases.title',
        title: translate('widgets.phrases'),
        display: isNlpFieldDisplayed('analyses.phrases.title'),
      },
      {
        id: 'analyses.taxonomy.taxonomyFullPath',
        title: translate('widgets.taxonomy'),
        display: isNlpFieldDisplayed('analyses.taxonomy.taxonomyFullPath'),
      },
      {
        id: 'analyses.themes.normalizedTitle',
        title: translate('widgets.themes'),
        display: isNlpFieldDisplayed('analyses.themes.normalizedTitle'),
      },
      {
        id: 'analyses.topics.title',
        title: translate('widgets.topics'),
        display: isNlpFieldDisplayed('analyses.topics.title'),
      },
      {
        id: 'analyses.sentimentPolarity',
        title: translate('widgets.document-sentiment'),
        display: isNlpFieldDisplayed('analyses.sentimentPolarity'),
      },
    ];
    sortFields(documentListWidget.payload.documentViewer?.nlpFields, availableNlpFields);

    const isMetadataFieldDisplayed = isFieldDisplayedFn(
      documentListWidget.payload.documentViewer?.metadataFields
    );
    const availableMetadataFields = documentListWidget.otherFields
      .filter(
        (field) =>
          (field.property !== 'sentiment' && field.property !== 'locationId' && field.available) ||
          field.property === 'tags'
      )
      .map((field) => ({
        id: field.property,
        title: field.label,
        display: isMetadataFieldDisplayed(field.property),
      }));
    const tagsIdx = availableMetadataFields.findIndex((f) => f.id === 'tags');
    const [tagElement] = availableMetadataFields.splice(tagsIdx, 1);
    availableMetadataFields.push(tagElement);
    sortFields(documentListWidget.payload.documentViewer?.metadataFields, availableMetadataFields);

    this.docListSettingsOptions = {
      textFields,
      otherFields,
      isClickOpenDocument: documentListWidget.payload.isClickOpenDocument,
      columnDisplayLabels: documentListWidget.payload.columnDisplayLabels,
      useFieldNames: documentListWidget.payload.useFieldNames,
      showNlpData: documentListWidget.payload.documentViewer?.showNlpData,
      availableNlpFields,
      showMetadata: documentListWidget.payload.documentViewer?.showMetadata,
      showQuickActions: documentListWidget.payload.documentViewer?.showQuickActions,
      quickActions: documentListWidget.payload.documentViewer?.quickActions,
      availableMetadataFields,
      documentListWidget,
    };
    this.showDocListSettingsModal = true;
  }

  onCloseDocListSettingsModal(result: DocumentListSettingsDialogData) {
    this.showDocListSettingsModal = false;
    if (result) {
      result.documentListWidget.setDisplayColumns(result.textFields, result.otherFields);
      result.documentListWidget.payload.displayColumns = [
        ...result.textFields.filter((c) => c.display).map((c) => c.columnId),
        ...result.otherFields.filter((c) => c.display).map((c) => c.columnId),
      ];
      result.documentListWidget.payload.isClickOpenDocument = result.isClickOpenDocument;
      result.documentListWidget.payload.columnDisplayLabels = result.columnDisplayLabels;
      result.documentListWidget.payload.useFieldNames = result.useFieldNames;
      result.documentListWidget.payload.documentViewer = {
        showNlpData: result.showNlpData,
        nlpFields: result.nlpFields,
        showMetadata: result.showMetadata,
        metadataFields: result.metadataFields,
        showQuickActions: result.showQuickActions,
        quickActions: result.quickActions,
      };
      this.dashboardStore.markDirty();
    }
  }

  async openSaveDocumentsToCollection(
    destinationType: 'NEW' | 'EXISTING',
    source: 'WIDGET' | 'REPORT',
    initialQuery?: string,
    explicitPointName?: string
  ) {
    if (!this.currentFilteredDocumentCount) {
      return;
    }

    let query = initialQuery;
    if (!query) {
      query = this.getQuery(explicitPointName);
    }

    this.saveDocumentsToCollectionModalOptions = {
      projectId: this.widget.payload.projectId,
      widgetDataSource: this.widgetDataSource,
      query,
      destinationType,
      source,
    };
    this.showSaveDocumentsToCollectionModal = true;
  }

  onSaveDocumentsToCollectionClosed(_$event) {
    this.showSaveDocumentsToCollectionModal = false;
  }

  private updateDriverAnalysisProperties(dataSource: WidgetDataSource) {
    this.account.sub.latest().then((acc) => {
      this.driverAnalysisTooltip = undefined;
      this.hasDriverAnalysisFeature = acc.features.includes('DRIVER_ANALYSIS');

      if (!this.hasDriverAnalysisFeature) {
        this.driverAnalysisTooltip = translate('widgets.driver-analysis-permission-missing');
        return;
      }

      if (!this.canCreateKda) {
        this.driverAnalysisTooltip = translate(
          'widgets.kda-creation-datasource-lacks-requried-driver-questions'
        );
        return;
      }

      if (dataSource) {
        if (dataSource.type === 'group') {
          this.driverAnalysisTooltip = translate('widgets.driver-analysis-group');
          return;
        }

        this.driverAnalysisTooltip = ProjectCollection.setDriverAnalysisTooltip(
          { data: { locked: false }, analyses: [dataSource] },
          acc
        );
      }
    });
  }

  async onCreateDriverAnalysisClicked() {
    if (!this.canCreateKda) {
      return;
    }
    const kdaPreReq = await this.getKdaPrerequisites();
    const eventData: WidgetDriverAnalysisOptions = {
      dataSource: this.widgetDataSource,
      weightFields: this.elementComponent.weightFields,
      fieldStatisticPayload: this.fieldStatisticsPayload,
      projectCollection: kdaPreReq.projectCollection,
      query: this.getQuery(),
      filters: this.widget.payload.filters,
      userSearch: this.widget.payload.userSearch?.userSearch,
    };

    if (this.widgetGroup) {
      eventData.widgetGroupId = this.widgetGroup.id;
      eventData.widgetGroupFilters = copyWidgetGroupFilters(this.widgetGroup, this.widget.id);
    }

    if (this.comparison) {
      eventData.comparisonId = this.comparison.id;
      if (this.comparison.filters.length) {
        eventData.comparisonFilters = { ...this.comparison.filters[0] };
      }
    }

    this.openCreateDriverAnalysis.emit(eventData);
  }

  // END widget actions

  getReport(payload = this.widget.payload): AbstractChartReport {
    const reportOptions = JSON.parse(JSON.stringify(payload));
    reportOptions['widgetDataSource'] = this.widgetDataSource;
    FieldService.fillFieldDisplayNameForWidgets(reportOptions);
    return this.chartReportService.configureReport(reportOptions);
  }

  isChartWidget() {
    return (
      (!this.isShowAsDocumentList && this.widget.component === 'DashboardWidgetChartComponent') ||
      this.widget.component === 'DashboardWidgetTopMoversComponent'
    );
  }

  isDocumentList() {
    return (
      this.widget.component === 'DashboardWidgetDocumentListComponent' || this.isShowAsDocumentList
    );
  }
  isInsightFeed() {
    return (
      this.widget.component === 'DashboardWidgetInsightFeedComponent' ||
      this.widget.payload.type === 'insightFeed'
    );
  }

  documentListDisplayView(documentIconActive: boolean) {
    this.widget.payload.isCardView = documentIconActive;
    this.dashboardStore.markDirty();
  }

  hasChartOptions() {
    return (
      (this.isChartWidget() &&
        this.widget.payload.type !== 'taxonomyMap' &&
        this.widget.payload.type !== 'wordcloud') ||
      this.widget.payload.type === 'crosstab' ||
      this.isPredictive ||
      this.widget.payload.type === 'bigNumber' ||
      this.widget.payload.type === 'donut' ||
      this.widget.payload.type === 'gauge' ||
      this.widget.payload.type === 'grid' ||
      this.widget.component === 'DashboardWidgetJourneyComponent'
    );
  }

  canHideItems() {
    return (
      (this.isChartWidget() ||
        this.widget.payload.type === 'crosstab' ||
        this.widget.payload.type === 'impact' ||
        this.widget.payload.type === 'predictor' ||
        this.widget.payload.type === 'featureScoring') &&
      this.widget.payload.type !== 'numericMetadata' &&
      this.widget.payload.type !== 'donut' &&
      this.widget.payload.type !== 'gauge' &&
      !this.hasCustomSet()
    );
  }

  canFilterSentiment() {
    return (
      this.isChartWidget() &&
      this.widget.payload.type !== 'taxonomyMap' &&
      this.widget.payload.type !== 'numericMetadata' &&
      this.widget.payload.type !== 'scatterPlot' &&
      this.widget.payload.type !== 'donut' &&
      this.widget.payload.type !== 'gauge'
    );
  }

  canWeigh() {
    return (
      !this.isWidgetReadOnly &&
      this.widget.payload.type !== 'documents' &&
      this.widget.payload.type !== 'note' &&
      this.widget.payload.type !== 'noteMarkdown' &&
      this.widget.payload.type !== 'sentimentAccuracy' &&
      this.widget.payload.type !== 'taxonomyMap' &&
      this.widget.payload.type !== 'featureScoring' &&
      this.widget.payload.type !== 'predictor' &&
      this.widget.payload.type !== 'insightFeed'
    );
  }

  get weightFields() {
    return this.elementComponent?.weightFields ?? [];
  }

  hasCustomSet() {
    if (!this.widget.payload.selectedItems) {
      return false;
    }
    return this.widget.payload.selectedItems.length > 0;
  }

  countSelectorsEnabled() {
    return this.widget.payload.type !== 'crosstab' || !this.widget.payload.selectedItems.length;
  }

  sentimentFilterEnabled() {
    return ['themes', 'phrases', 'topics', 'entities', 'taxonomy'].includes(
      this.widget.payload.analyticItem
    );
  }

  private beforePillCompactFn() {
    const self = this;
    return function ($event: { parentWidth: number; parentPadding: number }) {
      self.drawSubHeaderButtons($event);

      Object.keys(self.widgetActions).forEach(
        (action) => (self.widgetActions[action].header = true)
      );

      return Object.values(self.widgetActions).filter((action) => action.header).length * 36 + 36;
    };
  }

  private afterPillCompactFn() {
    const self = this;
    return function ($event: { parentWidth: number; parentPadding: number }) {
      self.drawSubHeaderButtons($event);

      return Object.values(self.widgetActions).filter((action) => action.header).length * 36 + 36;
    };
  }

  drawSubHeaderButtons($event: { parentWidth: number; parentPadding: number }) {
    if (
      !this.actionsWrapper ||
      !this.widgetContainer ||
      this.dashboard.locked ||
      this.widget.component === 'DashboardWidgetMarkdownComponent' ||
      this.widget.component === 'DashboardWidgetNotesComponent' ||
      this.widget.component === 'DashboardWidgetPredictiveComponent'
    ) {
      return;
    }

    const widgetContainerEl = this.widgetContainer.nativeElement;
    const actionButtons = this.actionsWrapper.nativeElement.getElementsByTagName('button');
    const menuButton = actionButtons[actionButtons.length - 1];
    if (menuButton) {
      const buttonWidth = menuButton.offsetWidth;
      const buttonCount = Object.keys(this.widgetActions).length;

      Object.keys(this.widgetActions).forEach(
        (action) => (this.widgetActions[action].header = false)
      );

      const otherElementsWidth = Array.from(
        widgetContainerEl.querySelectorAll(
          '.widget__sub-header-column:not(.widget__sub-header-column--actions)'
        )
      ).reduce((prev, curr: any) => prev + curr.offsetWidth, 0) as number;

      // formula is total_width - 2*padding - ellipsis_button_width - datasource|widgetgroup|comparison_width - EPS
      const remainingWidth =
        $event.parentWidth - 2 * $event.parentPadding - buttonWidth - otherElementsWidth;

      if (remainingWidth > 0) {
        const remainingSlots = Math.min(Math.floor(remainingWidth / buttonWidth), buttonCount);
        for (let i = remainingSlots; i > 0; i--) {
          Object.entries(this.widgetActions).find(([_key, value]) => value.order === i)[1].header =
            true;
        }
      }
    }
    this.showWidgetMenu = this.calculateShowWidgetMenu();
  }

  onCustomSortModeChanged($event) {
    this.isCustomSortMode = $event;
    this.isCustomSortModeChange.emit($event);
  }

  onSyncItemColors($event) {
    this.syncItemColors.emit($event);
  }

  onResetItemColor(pointId: string) {
    this.resetItemColor.emit(pointId);
  }

  clearCrosstabCustomSort() {
    if (this.isExpansionPanelShowing) {
      return;
    }

    this.onCustomSortModeChanged(false);
    (this.getComponent() as WidgetCrosstabComponent).clearSort();
  }

  enableCrosstabCustomSort() {
    this.onCustomSortModeChanged(true);
  }

  get widgetInfo() {
    return this.getComponent() && this.getComponent().widgetInfo;
  }

  openWidgetMenu($event) {
    $event.stopPropagation();

    this.widgetMenuTrigger?.openMenu();
  }

  private closeWidgetMenu() {
    this.widgetMenuTrigger?.closeMenu();
  }

  get nativeElement() {
    return this.elementRef.nativeElement;
  }

  ngOnDestroy() {
    this.unsub$.next();
    this.unsub$.complete();

    clearInterval(this.pillColoringInterval);
    this.bringWidgetBackAndResetSize(false);
  }

  get chartOptionsMaxWidth(): string {
    if (
      this.widget.payload.analyticItemTwo ||
      this.widget.payload.type === 'donut' ||
      this.widget.payload.type === 'gauge'
    ) {
      return '900px';
    }

    if (this.widget.payload.type === 'grid') {
      return '1200px';
    }

    return '600px';
  }

  private createCommentTarget(item): CommentTarget {
    if (!item) {
      return;
    }

    // When we add a comment from an already existing smart summary comment via the summary window
    if (item.analyticItems) {
      return item;
    }

    return {
      analyticItems: [...this.elementComponent.itemToCommentAnalyticItem(item)],
      ...this.elementComponent.itemToCommentPoint(item),
    };
  }

  openCommentDialog(
    item,
    initialContent?: { title?: string; summary?: string; details?: string },
    commentId?: string,
    type?: CommentType,
    smartSummaryId?: string
  ) {
    const comment = this.widget.payload.comments?.find((c) => c.id === commentId);

    const targetPoint = this.createCommentTarget(item);

    this.commentModalOptions = {
      targetPoint,
      comment,
      initialContent,
      type,
      smartSummaryId,
      smartSummaryExists: this.elementComponent.storedSmartSummaries.some(
        (ss) => ss.id === comment?.smartSummaryId
      ),
      commentChanges: this.elementComponent.getCommentChanges(),
      isCommentVisible: !comment || this.elementComponent.isCommentVisible(comment),
      isReadOnly: this.isReadOnly,
      isDashboardLocked: this.dashboard.locked,
      isPinningDisabled: this.widget.payload.type === 'focusAreas',
    };

    this.showEditCommentModal = true;
  }

  onOpenComment(comment: Comment): void {
    const commentToEdit = {
      title: comment.title,
      summary: comment.summary,
      details: comment.content,
    };
    this.openCommentDialog(null, commentToEdit, comment.id);
  }

  private updateCommentList(): void {
    this.commentsHandlerService.setComments(
      this.elementComponent.getCommentsForPanel(),
      this.elementComponent.getVisibleComments()
    );
    this.commentsHandlerService.setCommentChanges(this.elementComponent.getCommentChanges());
  }

  onCommentDialogClosed($event: { action: string; comment: Comment }) {
    this.showEditCommentModal = false;

    if ($event) {
      switch ($event.action) {
        case 'CREATE':
          this.elementComponent.addComment($event.comment);
          break;
        case 'DELETE':
          this.elementComponent.deleteComment($event.comment);
          break;
        case 'EDIT':
          this.elementComponent.editComment($event.comment);
          break;
      }
      this.updateCommentList();

      this.updateWidgetActionComments();
      this.dashboardStore.markDirty();
    }
  }

  onOpenSmartSummaryFromComment($event) {
    this.showEditCommentModal = false;
    this.elementComponent.openSmartSummaryById($event.smartSummaryId);
  }

  onAddSmartSummaryNotes($event) {
    const widget = this.widgetService.createNoteWidget(this.dashboard, $event);
    this.dashboardViewComponent.onWidgetAddDialogClosed(widget);
    this.alertService.showToast(translate('widgets.interactive-smart-summary.note-widget-added'));
  }

  private async getAnalyticItemsMap() {
    this.analyticItemTypes = await AnalyticItemTypes.generateMapFromDataSource(
      this.widgetDataSource,
      this.accountService,
      this.customFieldService
    );
  }

  get isNewspaperShowing() {
    return this.widget.payload.newspaper?.show && !this.isShowAsDataTable;
  }

  setAnnotationSelected(commentTarget: CommentTarget) {
    this.elementComponent.setAnnotationSelected(commentTarget);
  }

  get hasAiInsights() {
    return (
      this.widget.payload.type === 'crosstab' ||
      this.widget.payload.type === 'impact' ||
      this.widget.payload.type === 'sentimentVolume' ||
      this.widget.payload.type === 'scatterPlot' ||
      this.widget.payload.type === 'sentimentScore' ||
      this.widget.payload.type === 'sentimentPolarity' ||
      this.widget.payload.type === 'bigNumber' ||
      this.widget.payload.type === 'gauge' ||
      this.widget.payload.type === 'breakdown' ||
      this.widget.payload.type === 'donut' ||
      this.widget.payload.type === 'numericMetadata' ||
      this.widget.payload.type === 'documentCount'
    );
  }

  onAiInsightsOpenClicked() {
    this.beforeExpansionPanelOpen.emit(this.widget.id);
    this.commentsHandlerService.showExpansionPanel({
      widgetId: this.widget.id,
      widgetContainer: this.widgetComponent.widgetContainer,
      panelType: 'INSIGHTS',
    });
  }

  openAiInsights() {
    this.hideComments();
    this.bringWidgetForthAndIncreaseSize(this.widgetNeedsOrHasExtension);

    setTimeout(() => {
      this.recalculateContainerWidth(this.aiInsightsContainer);
      this.isWidgetDisabled = true;
    });

    this.aiInsightsOptions.widgetGroup = this.widgetGroup;
    this.aiInsightsOptions.dataPointCount = this.elementComponent.getAiInsightDataPointCount.bind(
      this.elementComponent
    );
    this.aiInsightsOptions.canGenerateInsights = this.authService.canGenerateRevealInsights();
    this.aiInsightsOptions.currentUserId = this.authService.currentUserId;
    this.aiInsightsSelectedPoints = [];
    this.showAiInsights = true;
  }

  onAiInsightsCloseClicked() {
    this.commentsHandlerService.hideExpansionPanel({
      widgetId: this.widget.id,
      panelType: 'INSIGHTS',
    });
  }

  closeAiInsights(): void {
    this.showAiInsights = false;

    this.elementComponent.endAiInsightPointSelectionFlow();

    this.bringWidgetBackAndResetSize();
    this.showComments();
  }

  async onAiInsightCreated($event: AiInsight) {
    if (!$event.insight_id) {
      return;
    }

    const widgetImage = (await this.elementComponent.exportAsImage()) as Blob;
    this.aiInsightsService.uploadImage(
      $event.insight_id,
      new File([widgetImage], `widget-${this.widget.id}-insight-${$event.insight_id}.png`, {
        type: widgetImage.type,
      })
    );
  }

  onChooseAiInsightPoints($event: MatchmakerHandler) {
    this.aiInsightsSelectedPoints = [];
    this.elementComponent.startAiInsightPointSelectionFlow($event);
  }

  onAutoChooseAiInsightPoints($event: MatchmakerHandler) {
    this.aiInsightsSelectedPoints = [];
    this.elementComponent.autoSelectAiInsightPoints($event);
  }

  onResetAiInsightPoints() {
    this.aiInsightsSelectedPoints = [];
    this.elementComponent.endAiInsightPointSelectionFlow();
  }

  onAiInsightPointSelection($event: { point: any | any[]; isSelected: boolean }[]) {
    for (const selection of $event) {
      if (selection.isSelected) {
        this.aiInsightsSelectedPoints = [...this.aiInsightsSelectedPoints, selection.point];
        continue;
      }

      this.aiInsightsSelectedPoints = this.aiInsightsSelectedPoints.filter(
        (p) => selection.point !== p
      );
    }
  }

  get isExpansionPanelShowing() {
    return this.showCommentList || this.showAiInsights;
  }

  private async getKdaPrerequisites() {
    const collection = await this.collectionService.getCollectionByName(
      this.widgetDataSource.collectionName,
      this.project.id
    );
    const projectCollection = new ProjectCollection(collection);
    projectCollection.analyses = this.datasourceManagerStore
      .getByCollectionName(this.widgetDataSource.collectionName)
      .filter((ds) => ds.type === 'analysis')
      .map((ds) => ({
        name: ds.id,
        displayName: ds.displayName,
        collectionName: ds.collectionName,
        modifiedAt: ds.modifiedAt,
        hasDateData: ds.hasDateData,
        hasTaxonomy: ds.hasTaxonomy,
        entityTypes: ds.entityTypes,
        topicTypes: ds.topicTypes,
        documentCount: ds.documentCount,
        hasEntities: ds.hasEntities,
        hasPhrases: ds.hasPhrases,
        hasThemes: ds.hasThemes,
        hasTopics: ds.hasTopics,
        hasTopicMentions: ds.hasTopicMentions,
        hasTopicPhrases: ds.hasTopicPhrases,
        hasTopicThemes: ds.hasTopicThemes,
        hasThemeMentions: ds.hasThemeMentions,
        hasEntityMentions: ds.hasEntityMentions,
        hasEntityPhrases: ds.hasEntityPhrases,
        customFields: ds.customFields,
        standardFields: ds.standardFields,
        sourceVersion: ds.sourceVersion,
        minDocAt: ds.minDocAt,
        maxDocAt: ds.maxDocAt,
        clauseModelTypes: ds.clauseModelTypes,
        configId: ds.configId,
        configName: ds.configName,
        notes: ds.notes,
        query: '',
        createdAt: ds.modifiedAt,
        status: undefined,
        statusText: '',
        progress: undefined,
        analysisCount: 0,
        inflightCount: 0,
        processedCount: 0,
        sentCount: 0,
        lastRunAt: ds.modifiedAt,
        isReAnalyze: false,
        embeddingStatus: ds.embeddingStatus,
        embeddingModel: ds.embeddingModel,
        embeddingStrategy: ds.embeddingStrategy,
        unAliasedCustomFields: ds.unAliasedCustomFields,
        unAliasedWeightFields: ds.unAliasedWeightFields,
      }));

    const query = this.getQuery();
    const customFields =
      // There are cases wehere we have empty analyses due to ds rules applied
      projectCollection.analyses[0]?.unAliasedCustomFields?.join() ||
      projectCollection.analyses[0]?.customFields?.join() ||
      '*';
    return { projectCollection, query, customFields };
  }

  private getQuery(explicitPointName?: string): string {
    const widgetQueryOptions = this.filterService.getWidgetQueryOptions(
      this.widget,
      this.widgetDataSource,
      this.widgetGroup,
      this.comparison?.filters[0]
    );
    widgetQueryOptions.pointName = explicitPointName ?? widgetQueryOptions.pointName;

    // DASH-10697: Spotlight does not support "In Sections" query clauses for standard/custom fields,
    // so section-aware query parts must only be applied through the guarded selector path.
    if (
      !!widgetQueryOptions.selectedSection?.length &&
      FilterUtils.isSectionSelectorAvailable(widgetQueryOptions.analyticItem)
    ) {
      widgetQueryOptions.userQuery = this.filterService.getAnalyticItemSectionInfoListQueryPart(
        widgetQueryOptions.analyticItem,
        widgetQueryOptions.selectedSection,
        null
      );
    }
    return this.filterService.getQueryForDocuments(widgetQueryOptions, false, true, true);
  }

  private async getFieldStatictics(
    projectCollection: ProjectCollection,
    query: string,
    customFields: string
  ): Promise<FieldStatisticsPayload> {
    return this.fieldStatisticsService.getStatsForCollection(
      this.project.id,
      projectCollection.data.collectionName,
      undefined,
      'xx', // std fields not needed
      customFields,
      undefined,
      query,
      this.timezoneService.timezone
    );
  }

  private getAvailableFieldsForKdaCreation(fieldStatisticsPayload: FieldStatisticsPayload) {
    return Object.keys(fieldStatisticsPayload.custom).filter(
      (fieldName) =>
        FieldService.isRating(fieldName) &&
        FieldService.isVisible(fieldName) &&
        Math.round(Utils.conversionRateFor(fieldName, fieldStatisticsPayload.custom)) >
          Constants.MIN_ALLOWED_CONVERSION_RATE
    );
  }

  setAnalyticItemModalMaxwidth(showSectionSelector: boolean) {
    this.analyticItemModalWidth.set(
      showSectionSelector ? '900px' : Constants.ANALYTIC_ITEM_MODAL_DEFAULT_MAX_WIDTH
    );
  }
}
